import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check } from "lucide-react";
import {
  COMMERCIAL_LOANS_OPTIONS,
  SUBMISSION_MODE_CORP_ONLY,
  SUBMISSION_MODE_FULL_GUARANTOR,
  commercialLoansAllowsCorpOnly,
  corpOnlyHardGatePasses,
  isCorpOnlyEligible,
  normalizeSubmissionMode,
} from "@/utils/corpOnlyIntake";
import { SiteLegalFooter } from "@/components/layout/site-legal-footer";

// US States and Territories list for dropdowns
const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
  { value: "DC", label: "District of Columbia" },
  { value: "PR", label: "Puerto Rico" },
  { value: "VI", label: "U.S. Virgin Islands" },
  { value: "AS", label: "American Samoa" },
  { value: "GU", label: "Guam" },
  { value: "MP", label: "Northern Mariana Islands" },
];

const CDL_EQUIPMENT_SLUGS = new Set(["sleeper-trucks-tractors", "day-cabs-non-sleeper-truck"]);
const LOGISTICS_INDUSTRY = "Logistics/Distribution";

type IntakeProgramConfig = {
  intake_profile?: string;
  lock_industry?: string;
  lock_equipment_type_slug?: string;
  default_equipment_name?: string;
  hide_equipment_hours?: boolean;
  hide_equipment_name?: boolean;
  hide_equipment_subtype?: boolean;
  equipment_fields?: string[];
  customer_logo_url?: string;
};

interface PublicIntakePageProps {
  isSalesRepLink?: boolean;
}

export function PublicIntakePage({ isSalesRepLink = false }: PublicIntakePageProps = {}) {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [showSignatureStep, setShowSignatureStep] = useState(false);
  /** True while POST submit is in flight after showing the signature panel (blocks sign until done). */
  const [submitAwaitingSignature, setSubmitAwaitingSignature] = useState(false);
  const [isCommercial, setIsCommercial] = useState(false);
  // DocuSign removed - no longer used in application submission
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const previousOwnersCountRef = useRef<number>(0);
  const [showFullTerms, setShowFullTerms] = useState(false);
  const [isInIframe, setIsInIframe] = useState(false);
  const formContainerRef = useRef<HTMLDivElement>(null);

  const toDigits = (v: string) => (v || "").replace(/\D/g, "");

  // Data and invite loaded first so any hook below can reference them without TDZ
  // intakeReady / intakePending: gate on isFetchedAfterMount so warm React Query cache +
  // background refetch does not flash an unlocked dropdown (isPending is false while refetching).
  const intakeQuery = useQuery({
    queryKey: ["intake-data", token ?? "", isSalesRepLink],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (token) {
        if (isSalesRepLink) params.set("rep_token", token);
        else params.set("invite_token", token);
      }
      const url = params.toString() ? `/api/intake/data/?${params.toString()}` : "/api/intake/data/";
      const { data } = await customInstance.get(url);
      return data as {
        industries: { id: number; name: string }[];
        consent_language?: { content: string };
        organization_type?: string | null;
        organization_logo_url?: string | null;
        equipment_types?: {
          id: number;
          name: string;
          slug: string;
          display_order: number;
          examples?: { id: number; label: string }[];
          subtypes?: { id: number; name: string }[];
        }[];
        primary_equipment_custom_field_defs?: { key: string; label: string }[];
        intake_program?: IntakeProgramConfig;
      };
    },
  });
  const intakeData = intakeQuery.data;
  const intakeReady = intakeQuery.isSuccess && intakeQuery.isFetchedAfterMount;
  const intakePending = !intakeReady;

  const {
    data: inviteData,
    isPending: inviteQueryPending,
    isFetched: inviteQueryFetched,
  } = useQuery({
    queryKey: ["invite", token],
    queryFn: async () => {
      const { data } = await customInstance.get(`/api/intake/invites/${token}/`);
      return data as {
        saved_data?: any;
        last_completed_step?: string;
        stage?: string;
        organization_logo_url?: string | null;
        equipment_locked?: boolean;
        intake_channel?: string;
        application_signed?: boolean;
        intake_phase?: "form" | "sign" | "equipment" | "thank_you" | "locked";
        apply_step?: string;
        equipment_intake_pending?: boolean;
        can_confirm_equipment?: boolean;
        show_prequals_after_submit?: boolean;
        intake_program?: IntakeProgramConfig;
      };
    },
    enabled: !!token && !isSalesRepLink, // Don't fetch invite for sales rep links
  });

  const intakeProgram: IntakeProgramConfig = useMemo(
    () => inviteData?.intake_program ?? intakeData?.intake_program ?? {},
    [inviteData?.intake_program, intakeData?.intake_program],
  );
  const isDemcoGrainCart = intakeProgram.intake_profile === "demco_grain_cart";

  /** Public apply: avoid DealerDog flash before we know org branding (invite and sales-rep links). */
  const organizationLogoUrl =
    intakeProgram.customer_logo_url ||
    intakeData?.organization_logo_url ||
    (!isSalesRepLink ? inviteData?.organization_logo_url : null) ||
    null;
  const brandingPlaceholder =
    Boolean(token) &&
    !organizationLogoUrl &&
    !(
      intakeQuery.isFetched &&
      (isSalesRepLink || inviteQueryFetched)
    );

  /** Staff “manual intake” link: same /apply flow, no drawn signature; credit runs on submit server-side */
  const isStaffManualIntakeInvite = inviteData?.intake_channel === "STAFF_PORTAL";

  // Lock equipment only when invite was created from portal (dealer "Create link"); not for org-link / dealer-mock / sales rep
  const isInviteLinkWithEquipment = !!token && !isSalesRepLink && inviteData?.equipment_locked === true;

  // Detect if we're in an iframe
  useEffect(() => {
    setIsInIframe(window.self !== window.top);
  }, []);

  // Resume routing from GET invite intake_phase (invite links only)
  useEffect(() => {
    if (!token || isSalesRepLink || !inviteQueryFetched || !inviteData?.intake_phase) return;
    const phase = inviteData.intake_phase;
    if (phase === "thank_you" || phase === "locked") {
      const params = new URLSearchParams({ token });
      navigate(`/thank-you?${params.toString()}`, { replace: true });
      return;
    }
    if (phase === "sign") {
      setShowSignatureStep(true);
      return;
    }
    if (phase === "equipment") {
      setShowSignatureStep(false);
      setStep(4);
    }
  }, [token, isSalesRepLink, inviteQueryFetched, inviteData?.intake_phase, navigate]);

  // Auto-scroll/center field on focus (for iframe)
  useEffect(() => {
    if (!isInIframe) return;

    const handleFieldFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      // Only handle input, select, textarea elements
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA') {
        setTimeout(() => {
          target.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
        }, 100);
      }
    };

    document.addEventListener('focusin', handleFieldFocus);
    return () => {
      document.removeEventListener('focusin', handleFieldFocus);
    };
  }, [isInIframe]);

  // Scroll to first error field
  const scrollToFirstError = () => {
    if (Object.keys(fieldErrors).length === 0) return;
    
    // Find first input/select with error class
    const errorInput = document.querySelector('input.border-red-500, select.border-red-500, textarea.border-red-500');
    
    if (errorInput) {
      setTimeout(() => {
        errorInput.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
        (errorInput as HTMLElement).focus();
      }, 100);
    } else {
      // Fallback: find first error message and scroll to its parent input
      const firstErrorMsg = document.querySelector('.text-red-500.text-sm');
      if (firstErrorMsg) {
        const parentDiv = firstErrorMsg.closest('div');
        const input = parentDiv?.querySelector('input, select, textarea');
        if (input) {
          setTimeout(() => {
            input.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
            (input as HTMLElement).focus();
          }, 100);
        }
      }
    }
  };
  
  // Generate unique session_id for sales rep/admin/FI links to support multiple concurrent users
  // Store in useRef to persist across re-renders but not trigger re-renders
  const sessionIdRef = useRef<string | null>(null);
  if (isSalesRepLink && !sessionIdRef.current) {
    // Generate UUID v4
    sessionIdRef.current = crypto.randomUUID();
  }
  
  // DocuSign removed - no message listener needed
  
  // Helper to handle phone input with max 10 digits
  const handlePhoneChange = (value: string, fieldName: string = 'phone', setter: (val: any) => void) => {
    const digits = toDigits(value);
    // Strip leading "1" if present (US country code)
    let cleaned = digits;
    if (digits.length === 11 && digits.startsWith('1')) {
      cleaned = digits.slice(1);
    }
    // Limit to 10 digits
    const limited = cleaned.slice(0, 10);
    // Format as XXX-XXX-XXXX
    const formatted = limited.length > 6 
      ? `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`
      : limited.length > 3
      ? `${limited.slice(0, 3)}-${limited.slice(3)}`
      : limited;
    setter(formatted);
    // Clear field error when user starts typing
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Helper to handle Tax ID input (EIN) - format: xx-xxxxxxx (max 9 digits)
  const handleTaxIdChange = (value: string, setter: (val: any) => void) => {
    const digits = toDigits(value);
    // Limit to 9 digits
    const limited = digits.slice(0, 9);
    // Format as xx-xxxxxxx
    const formatted = limited.length > 2
      ? `${limited.slice(0, 2)}-${limited.slice(2)}`
      : limited;
    setter(formatted);
    // Clear field error when user starts typing
    if (fieldErrors.tax_id) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.tax_id;
        return newErrors;
      });
    }
  };

  // Helper to handle SSN input - format: xxx-xx-xxxx (max 9 digits)
  const handleSSNChange = (value: string, setter: (val: any) => void) => {
    const digits = toDigits(value);
    // Limit to 9 digits
    const limited = digits.slice(0, 9);
    // Format as xxx-xx-xxxx
    const formatted = limited.length > 5
      ? `${limited.slice(0, 3)}-${limited.slice(3, 5)}-${limited.slice(5)}`
      : limited.length > 3
      ? `${limited.slice(0, 3)}-${limited.slice(3)}`
      : limited;
    setter(formatted);
    // Clear field error when user starts typing
    if (fieldErrors.ssn) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.ssn;
        return newErrors;
      });
    }
  };

  // Helper to handle DOB input - format: MM/DD/YYYY (max 10 characters with slashes)
  const handleDOBChange = (value: string, setter: (val: any) => void, errorKey?: string) => {
    // Remove everything except digits and slashes
    const cleaned = value.replace(/[^\d/]/g, '');
    
    // Extract just the digits for length limiting
    const digits = toDigits(cleaned);
    
    // If user typed slashes, try to preserve them in valid positions
    if (cleaned.includes('/')) {
      // Match pattern: digits, optional slash, digits, optional slash, digits
      const parts = cleaned.split('/');
      const month = parts[0]?.slice(0, 2) || '';
      const day = parts[1]?.slice(0, 2) || '';
      const year = parts[2]?.slice(0, 4) || '';
      
      // Build formatted string, adding slashes at appropriate positions
      let formatted = month;
      if (month.length === 2 || day.length > 0) {
        formatted += '/';
        formatted += day;
        if (day.length === 2 || year.length > 0) {
          formatted += '/';
          formatted += year;
        }
      }
      
      // Limit total length to 10 characters (MM/DD/YYYY)
      if (formatted.length > 10) {
        formatted = formatted.slice(0, 10);
      }
      
      setter(formatted);
    } else {
      // No slashes - auto-format as user types digits
      const limited = digits.slice(0, 8);
      let formatted = '';
      if (limited.length > 4) {
        formatted = `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
      } else if (limited.length > 2) {
        formatted = `${limited.slice(0, 2)}/${limited.slice(2)}`;
      } else {
        formatted = limited;
      }
      setter(formatted);
    }
    
    // Clear field error when user starts typing
    if (errorKey && fieldErrors[errorKey]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Helper to validate DOB format and validity (MM/DD/YYYY)
  const validateDOB = (dob: string): string | null => {
    if (!dob || !dob.trim()) {
      return "Date of birth is required";
    }
    
    // Check format: MM/DD/YYYY
    const dobRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dob.match(dobRegex);
    
    if (!match) {
      return "Date of birth must be in MM/DD/YYYY format";
    }
    
    const month = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    
    // Validate month (1-12)
    if (month < 1 || month > 12) {
      return "Month must be between 01 and 12";
    }
    
    // Validate day (1-31, we'll let Date object handle specific month limits)
    if (day < 1 || day > 31) {
      return "Day must be between 01 and 31";
    }
    
    // Validate year (reasonable range, e.g., 1900-2100)
    if (year < 1900 || year > 2100) {
      return "Year must be between 1900 and 2100";
    }
    
    // Create a date object to validate actual date (handles leap years, days per month, etc.)
    const dateObj = new Date(year, month - 1, day);
    if (dateObj.getFullYear() !== year || dateObj.getMonth() !== month - 1 || dateObj.getDate() !== day) {
      return "Invalid date (e.g., February 30th doesn't exist)";
    }
    
    // Check if date is in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dateObj > today) {
      return "Date of birth cannot be in the future";
    }
    
    return null; // Valid DOB
  };

  // Helper to convert MM/DD/YYYY to YYYY-MM-DD for backend
  const convertDOBForBackend = (dob: string): string => {
    if (!dob || !dob.trim()) return '';
    // If already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) return dob;
    // Remove all non-digits
    const digits = toDigits(dob);
    if (digits.length !== 8) return dob; // Return as-is if not 8 digits
    // Format: MMDDYYYY -> YYYY-MM-DD
    const month = digits.slice(0, 2);
    const day = digits.slice(2, 4);
    const year = digits.slice(4, 8);
    return `${year}-${month}-${day}`;
  };

  // Helper to convert YYYY-MM-DD to MM/DD/YYYY for display
  const convertDOBForDisplay = (dob: string): string => {
    if (!dob || !dob.trim()) return '';
    // If already in MM/DD/YYYY format, return as-is
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dob)) return dob;
    // Try to parse YYYY-MM-DD format
    const match = dob.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, year, month, day] = match;
      return `${month}/${day}/${year}`;
    }
    return dob; // Return as-is if format is unknown
  };

  // Basic form state (simplified)
  const formatPhone = (raw: string) => {
    // Keep digits, remove leading country code 1/+1
    let digits = (raw || "").replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
    if (digits.length === 12 && digits.startsWith("01")) digits = digits.slice(2);
    digits = digits.slice(0, 10);
    const part1 = digits.slice(0, 3);
    const part2 = digits.slice(3, 6);
    const part3 = digits.slice(6, 10);
    if (part3) return `${part1}-${part2}-${part3}`;
    if (part2) return `${part1}-${part2}`;
    return part1;
  };

  const formatCurrency = (raw: string) => {
    const digits = toDigits(raw);
    const num = Number(digits || 0);
    return num.toLocaleString();
  };
  const normalizeCurrencyForSubmit = (raw: string) => {
    if (!raw || raw.trim() === "") return "";
    const cleaned = raw.toString().replace(/[^\d.]/g, "");
    if (!cleaned || cleaned === "" || cleaned === ".") return "";
    const num = parseFloat(cleaned);
    if (isNaN(num) || num <= 0) return "";
    return String(Math.floor(num));
  };

  const [form, setForm] = useState<any>({
    // Step 1: Contact Info & Equipment
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    is_business_owner: true, // Step-1 contact is always treated as a business owner
    equipment_type_id: null as number | null,
    equipment_subtype_id: null as number | null,
    equipment_name: "",
    equipment_price: "",
    equipment_year: "" as string | number,
    equipment_mileage: "",
    equipment_hours: "",
    primary_equipment_custom_values: {} as Record<string, string>,
    // Step 2A: Company Information
    is_commercial: true, // Default to Commercial Financing
    company_name: "",
    entity_type: "", // No default - force selection
    has_dba: false,
    company_dba: "",
    company_address_1: "",
    company_address_2: "",
    company_city: "",
    company_state: "",
    company_postal_code: "",
    company_phone: "",
    tax_id: "",
    years_in_business: "",
    industry: "", // No default - customer must select
    annual_revenue: "",
    owners_count: undefined, // New: "How many owners own more than 20%?" (includes primary) - no default to force selection
    // Step 3: Owners (replaces additional_owners, now includes primary)
    owners: [] as any[],
    // Legacy fields for backward compatibility
    additional_owners: [] as any[],
    additional_owners_count: 0,
    is_only_25_percent_owner: true,
    ownership_percent: "",
    // Personal-finance path (hidden, kept for compatibility)
    annual_verifiable_income: "",
    // Prequal CDL / SAFER gates (Step 3; persisted as Application.prequal_gate_answers)
    prequal_gate_cdl_held: undefined as "yes" | "no" | undefined,
    prequal_gate_cdl_over_one_year: undefined as "yes" | "no" | undefined,
    prequal_gate_first_time_oo: undefined as "yes" | "no" | undefined,
    prequal_gate_fleet_total: "",
    prequal_gate_safer_all: undefined as "yes" | "no" | undefined,
    prequal_gate_safer_reporting: "",
    // Corp-only path (Step 3)
    submission_mode: SUBMISSION_MODE_FULL_GUARANTOR as string,
    commercial_loans_completed: "" as string,
  });

  // Auto-populate form from saved data (run only when saved_data actually changes to avoid update loops)
  const savedDataFingerprintRef = useRef<string | null>(null);
  /** True after user picks equipment type (search or dropdown); blocks marketplace category-hint auto-select. */
  const equipmentTypeUserTouchedRef = useRef(false);
  /** True after we attempted embed category-hint resolution once for the current hint string. */
  const categoryHintResolvedRef = useRef(false);
  const categoryHintAttemptedForRef = useRef<string | null>(null);
  const prevApplyStepRef = useRef(step);
  useEffect(() => {
    const saved = inviteData?.saved_data;
    if (!saved || typeof saved !== 'object') return;
    const fingerprint = `${inviteData?.last_completed_step ?? ''}-${JSON.stringify({
      email: (saved as any).email,
      equipment_type_id: (saved as any).equipment_type_id,
      equipment_name: (saved as any).equipment_name,
      pecv: (saved as any).primary_equipment_custom_values,
    })}`;
    if (savedDataFingerprintRef.current === fingerprint) return;
    savedDataFingerprintRef.current = fingerprint;

    const isCommercial = (saved as any).is_commercial === true ? true : true; // Always default to Commercial
    const savedWithFormattedDOB = { ...(saved as Record<string, unknown>) };
    if ((saved as any).owners && Array.isArray((saved as any).owners)) {
      (savedWithFormattedDOB as any).owners = (saved as any).owners.map((o: any) => ({
        ...o,
        dob: o.dob ? convertDOBForDisplay(o.dob) : '',
      }));
    }
    if ((saved as any).additional_owners && Array.isArray((saved as any).additional_owners)) {
      (savedWithFormattedDOB as any).additional_owners = (saved as any).additional_owners.map((o: any) => ({
        ...o,
        dob: o.dob ? convertDOBForDisplay(o.dob) : '',
      }));
    }

    if ((saved as any).equipment_type_id != null) {
      equipmentTypeUserTouchedRef.current = true;
    }

    setForm((prev: any) => {
      const savedSanitized: Record<string, unknown> = { ...savedWithFormattedDOB };
      // Backend sends equipment_type_id / equipment_subtype_id as null for new apps; do not let that
      // clobber client-set values (e.g. marketplace category-hint pre-fill) before the user saves.
      if (savedSanitized.equipment_type_id == null) {
        delete savedSanitized.equipment_type_id;
      }
      if (savedSanitized.equipment_subtype_id == null) {
        delete savedSanitized.equipment_subtype_id;
      }

      const updated = {
        ...prev,
        ...savedSanitized,
        is_commercial: isCommercial,
        submission_mode: normalizeSubmissionMode(
          (savedSanitized as any).submission_mode ?? prev.submission_mode,
        ),
        commercial_loans_completed:
          (savedSanitized as any).commercial_loans_completed ?? prev.commercial_loans_completed ?? "",
        owners: (savedWithFormattedDOB as any).owners || (savedWithFormattedDOB as any).additional_owners || [],
      };
      if ((savedWithFormattedDOB as any).equipment_price != null && String((savedWithFormattedDOB as any).equipment_price).trim() !== "") {
        const priceNum = parseFloat(String((savedWithFormattedDOB as any).equipment_price)) || 0;
        if (priceNum > 0) {
          updated.equipment_price = String(Math.floor(priceNum));
        }
      } else {
        const equip0 = (saved as any).equipments?.[0];
        const equipPrice = equip0?.price;
        if (equipPrice != null && String(equipPrice).trim() !== "") {
          const priceNum = parseFloat(String(equipPrice)) || 0;
          if (priceNum > 0) {
            updated.equipment_price = String(Math.floor(priceNum));
          }
        }
      }
      if ((savedWithFormattedDOB as any).equipment_type_id != null) {
        updated.equipment_type_id = (savedWithFormattedDOB as any).equipment_type_id;
      }
      if ((savedWithFormattedDOB as any).equipment_subtype_id != null) {
        updated.equipment_subtype_id = (savedWithFormattedDOB as any).equipment_subtype_id;
      }
      if ((savedWithFormattedDOB as any).equipment_year != null && (savedWithFormattedDOB as any).equipment_year !== '') {
        updated.equipment_year = (savedWithFormattedDOB as any).equipment_year;
      }
      if ((savedWithFormattedDOB as any).equipment_mileage != null && (savedWithFormattedDOB as any).equipment_mileage !== '') {
        updated.equipment_mileage = String((savedWithFormattedDOB as any).equipment_mileage);
      }
      if ((savedWithFormattedDOB as any).equipment_hours != null && (savedWithFormattedDOB as any).equipment_hours !== '') {
        updated.equipment_hours = String((savedWithFormattedDOB as any).equipment_hours);
      }
      const pecv = (saved as any).primary_equipment_custom_values;
      if (pecv && typeof pecv === "object") {
        updated.primary_equipment_custom_values = {
          ...(prev.primary_equipment_custom_values || {}),
          ...pecv,
        };
      }
      const pga = (saved as any).prequal_gate_answers;
      if (pga && typeof pga === "object") {
        updated.prequal_gate_cdl_held = pga.cdl_held === "yes" || pga.cdl_held === "no" ? pga.cdl_held : undefined;
        const over = pga.cdl_held_over_one_year ?? pga.cdl_held_one_year;
        updated.prequal_gate_cdl_over_one_year = over === "yes" || over === "no" ? over : undefined;
        const firstTime = pga.first_time_buyer ?? pga.first_time_owner_operator;
        updated.prequal_gate_first_time_oo =
          firstTime === "yes" || firstTime === "no" ? firstTime : undefined;
        const totalOp = pga.safer_total_trucks_operated;
        const sfu = pga.safer_fleet_units;
        const allRep = pga.safer_all_reporting;
        if (allRep === "yes") {
          updated.prequal_gate_fleet_total = totalOp != null ? String(totalOp) : sfu != null ? String(sfu) : "";
          updated.prequal_gate_safer_all = "yes";
          updated.prequal_gate_safer_reporting = "";
        } else if (allRep === "no") {
          updated.prequal_gate_fleet_total = totalOp != null ? String(totalOp) : "";
          updated.prequal_gate_safer_reporting = sfu != null ? String(sfu) : "";
          updated.prequal_gate_safer_all = "no";
        }
      }
      return updated;
    });
    if (inviteData?.intake_phase === "equipment") {
      setShowSignatureStep(false);
      setStep(4);
    } else if (inviteData?.intake_phase === "sign") {
      setShowSignatureStep(true);
    } else if (inviteData?.last_completed_step) {
      if (inviteData.last_completed_step === "step_1_personal_info") setStep(2);
      else if (inviteData.last_completed_step === "step_2_commercial") setStep(3);
      else if (inviteData.last_completed_step === "step_4_equipment") setStep(4);
      else if (inviteData.last_completed_step === "submitted" && inviteData.intake_phase === "equipment") {
        setStep(4);
      } else setStep(1);
    }
  }, [inviteData?.saved_data, inviteData?.last_completed_step, inviteData?.intake_phase]);

  // Step-1 contact is always a business owner (after intake + saved_data hydrate).
  useEffect(() => {
    if (!intakeReady) return;
    setForm((prev) =>
      prev.is_business_owner === true ? prev : { ...prev, is_business_owner: true },
    );
  }, [intakeReady, inviteData?.saved_data]);

  // Fetch equipment types for dropdown when not provided by intake data (e.g. no token)
  const { data: equipmentTypesData } = useQuery({
    queryKey: ["equipment-types"],
    queryFn: async () => {
      const { data } = await customInstance.get("/api/equipment-types/");
      return Array.isArray(data) ? data : (data?.results ?? []);
    },
    enabled: intakeReady && !(intakeData?.equipment_types && intakeData.equipment_types.length > 0),
  });
  const equipmentTypes = useMemo(
    () => (intakeData?.equipment_types?.length ? intakeData.equipment_types : equipmentTypesData) ?? [],
    [intakeData?.equipment_types, equipmentTypesData]
  );

  // Org allows exactly one equipment type (intake/data already filtered): lock type like portal "Create link"
  const isSingleOrgEquipmentTypeOnly =
    intakeReady && intakeData?.equipment_types?.length === 1 && !isInviteLinkWithEquipment;
  /** Intake knows we're single-type but the lock effect hasn't committed form.equipment_type_id yet (cold incognito). */
  const singleTypeLockPending =
    intakeReady &&
    intakeData?.equipment_types?.length === 1 &&
    form.equipment_type_id == null &&
    !isInviteLinkWithEquipment;
  const isEquipmentTypeSelectorLocked =
    isInviteLinkWithEquipment ||
    intakePending ||
    isSingleOrgEquipmentTypeOnly ||
    singleTypeLockPending ||
    Boolean(intakeProgram.lock_equipment_type_slug);

  useEffect(() => {
    if (!intakeProgram.lock_industry) return;
    setForm((prev: any) =>
      prev.industry === intakeProgram.lock_industry
        ? prev
        : { ...prev, industry: intakeProgram.lock_industry },
    );
  }, [intakeProgram.lock_industry]);

  useEffect(() => {
    if (!intakeProgram.default_equipment_name) return;
    setForm((prev: any) =>
      prev.equipment_name === intakeProgram.default_equipment_name
        ? prev
        : { ...prev, equipment_name: intakeProgram.default_equipment_name },
    );
  }, [intakeProgram.default_equipment_name]);

  useEffect(() => {
    const slug = intakeProgram.lock_equipment_type_slug;
    if (!intakeReady || !slug || equipmentTypes.length === 0) return;
    const match = equipmentTypes.find((et: { slug?: string }) => et.slug === slug);
    if (!match) return;
    setForm((prev: any) =>
      prev.equipment_type_id === match.id ? prev : { ...prev, equipment_type_id: match.id },
    );
  }, [intakeReady, intakeProgram.lock_equipment_type_slug, equipmentTypes]);

  useEffect(() => {
    if (!intakeReady || !isSingleOrgEquipmentTypeOnly || equipmentTypes.length !== 1) return;
    const sole = equipmentTypes[0] as { id: number; subtypes?: { id: number; name: string }[] };
    const subtypes = sole.subtypes ?? [];
    const singleSubtypeId = subtypes.length === 1 ? subtypes[0].id : null;
    setForm((prev: any) => {
      const nextSubtype =
        singleSubtypeId != null
          ? singleSubtypeId
          : prev.equipment_subtype_id != null &&
              subtypes.some((s: { id: number }) => s.id === prev.equipment_subtype_id)
            ? prev.equipment_subtype_id
            : null;
      if (prev.equipment_type_id === sole.id && prev.equipment_subtype_id === nextSubtype) return prev;
      return { ...prev, equipment_type_id: sole.id, equipment_subtype_id: nextSubtype };
    });
  }, [intakeReady, isSingleOrgEquipmentTypeOnly, equipmentTypes]);

  // Re-attempt embed category-hint resolution when customer lands on Step 4 (saved_data may have loaded after sign).
  useEffect(() => {
    if (prevApplyStepRef.current !== 4 && step === 4) {
      categoryHintResolvedRef.current = false;
      categoryHintAttemptedForRef.current = null;
    }
    prevApplyStepRef.current = step;
  }, [step]);

  // Embed listing category hint (URL param or saved pecv) → resolve global type if allowed for org.
  useEffect(() => {
    if (!intakeReady || !intakeData) return;
    if (isInviteLinkWithEquipment || isSingleOrgEquipmentTypeOnly) return;
    if (form.equipment_type_id != null) return;
    if (equipmentTypeUserTouchedRef.current) return;
    const urlHint = (searchParams.get("equipment_category_hint") || "").trim();
    const pecv =
      (form.primary_equipment_custom_values as Record<string, unknown> | undefined) ??
      (inviteData?.saved_data as { primary_equipment_custom_values?: Record<string, unknown> } | undefined)
        ?.primary_equipment_custom_values;
    const pecvHint = typeof pecv?.__embed_category_hint === "string" ? pecv.__embed_category_hint.trim() : "";
    const hint = urlHint || pecvHint;
    if (!hint) return;
    if (categoryHintResolvedRef.current && categoryHintAttemptedForRef.current === hint) return;
    categoryHintAttemptedForRef.current = hint;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await customInstance.get<{
          equipment_type_id: number | null;
          equipment_type_name: string | null;
        }>("/api/intake/equipment-type-from-hint/", { params: { hint } });
        if (cancelled) return;
        const resolvedId = data?.equipment_type_id;
        if (resolvedId == null) return;
        const allowed = new Set((intakeData.equipment_types ?? []).map((et: { id: number }) => et.id));
        if (!allowed.has(resolvedId)) return;
        setForm((prev: any) => {
          if (prev.equipment_type_id != null) return prev;
          if (equipmentTypeUserTouchedRef.current) return prev;
          const matched = (intakeData.equipment_types ?? []).find((et: { id: number }) => et.id === resolvedId);
          const subtypes = matched?.subtypes ?? [];
          const singleSubtypeId = subtypes.length === 1 ? subtypes[0].id : null;
          categoryHintResolvedRef.current = true;
          return {
            ...prev,
            equipment_type_id: resolvedId,
            equipment_subtype_id: singleSubtypeId,
          };
        });
      } catch {
        /* leave picker empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    intakeReady,
    intakeData?.equipment_types,
    searchParams,
    isInviteLinkWithEquipment,
    isSingleOrgEquipmentTypeOnly,
    form.equipment_type_id,
    form.primary_equipment_custom_values,
    inviteData?.saved_data,
    step,
  ]);

  const selectedEquipmentSlug = useMemo(() => {
    const et = equipmentTypes.find((e: { id: number; slug?: string }) => e.id === form.equipment_type_id);
    return et?.slug ?? null;
  }, [equipmentTypes, form.equipment_type_id]);

  const showCdlGateQuestions = Boolean(selectedEquipmentSlug && CDL_EQUIPMENT_SLUGS.has(selectedEquipmentSlug));
  const showFleetGateQuestions = showCdlGateQuestions || form.industry === LOGISTICS_INDUSTRY;
  const fleetGateTruckCount = useMemo(
    () => parseInt(String(form.prequal_gate_fleet_total).replace(/\D/g, "") || "0", 10),
    [form.prequal_gate_fleet_total]
  );
  const ownerCountForCdlCopy = form.owners_count ?? (form.owners?.length || 1);
  const cdlQuestionsMultiOwner = ownerCountForCdlCopy >= 2;

  const showCommercialLoansQuestion = useMemo(
    () => corpOnlyHardGatePasses(form.years_in_business, form.entity_type),
    [form.years_in_business, form.entity_type],
  );
  const showCorpOnlyFork = useMemo(
    () =>
      showCommercialLoansQuestion &&
      commercialLoansAllowsCorpOnly(form.commercial_loans_completed),
    [showCommercialLoansQuestion, form.commercial_loans_completed],
  );
  const isCorpOnlySelected =
    normalizeSubmissionMode(form.submission_mode) === SUBMISSION_MODE_CORP_ONLY;

  useEffect(() => {
    const slug = equipmentTypes.find((e: { id: number; slug?: string }) => e.id === form.equipment_type_id)?.slug;
    const needsCdl = Boolean(slug && CDL_EQUIPMENT_SLUGS.has(slug));
    const needsFleet = needsCdl || form.industry === LOGISTICS_INDUSTRY;
    setForm((prev) => {
      let changed = false;
      const next = { ...prev };
      if (!needsCdl && (prev.prequal_gate_cdl_held !== undefined || prev.prequal_gate_cdl_over_one_year !== undefined || prev.prequal_gate_first_time_oo !== undefined)) {
        next.prequal_gate_cdl_held = undefined;
        next.prequal_gate_cdl_over_one_year = undefined;
        next.prequal_gate_first_time_oo = undefined;
        changed = true;
      }
      if (
        !needsFleet &&
        (String(prev.prequal_gate_fleet_total || "").trim() !== "" ||
          prev.prequal_gate_safer_all !== undefined ||
          String(prev.prequal_gate_safer_reporting || "").trim() !== "")
      ) {
        next.prequal_gate_fleet_total = "";
        next.prequal_gate_safer_all = undefined;
        next.prequal_gate_safer_reporting = "";
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [form.equipment_type_id, form.industry, equipmentTypes]);

  useEffect(() => {
    const defs = intakeData?.primary_equipment_custom_field_defs ?? [];
    if (!defs.length) return;
    setForm((prev: any) => {
      const cur = { ...(prev.primary_equipment_custom_values || {}) };
      let changed = false;
      for (const d of defs) {
        if (cur[d.key] === undefined) {
          cur[d.key] = "";
          changed = true;
        }
      }
      return changed ? { ...prev, primary_equipment_custom_values: cur } : prev;
    });
  }, [intakeData?.primary_equipment_custom_field_defs]);

  // When form is hydrated from saved data with an equipment type that has only one subtype, fill subtype if missing
  useEffect(() => {
    if (!form.equipment_type_id || form.equipment_subtype_id != null || equipmentTypes.length === 0) return;
    const selectedType = equipmentTypes.find((et: { id: number; subtypes?: { id: number; name: string }[] }) => et.id === form.equipment_type_id);
    const subtypes = selectedType?.subtypes ?? [];
    if (subtypes.length === 1) {
      setForm((prev: any) => ({ ...prev, equipment_subtype_id: subtypes[0].id }));
    }
  }, [equipmentTypes, form.equipment_type_id, form.equipment_subtype_id]);

  // Equipment search by synonym/name (debounced)
  const [equipmentSearchQuery, setEquipmentSearchQuery] = useState("");
  const [equipmentSearchResults, setEquipmentSearchResults] = useState<{ equipment_type: { id: number; name: string }; equipment_subtype: { id: number; name: string } | null }[]>([]);
  const equipmentSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!equipmentSearchQuery.trim()) {
      setEquipmentSearchResults([]);
      return;
    }
    if (equipmentSearchDebounceRef.current) clearTimeout(equipmentSearchDebounceRef.current);
    equipmentSearchDebounceRef.current = setTimeout(() => {
      const params: { q: string; allowed_types?: string } = { q: equipmentSearchQuery.trim() };
      if (equipmentTypes?.length) {
        params.allowed_types = equipmentTypes.map((et: { id: number }) => et.id).join(",");
      }
      customInstance.get<{ options: { equipment_type: { id: number; name: string }; equipment_subtype: { id: number; name: string } | null }[] }>(
        "/api/equipment-types/search/",
        { params }
      ).then(({ data }) => {
        setEquipmentSearchResults(data?.options ?? []);
      }).catch(() => setEquipmentSearchResults([]));
      equipmentSearchDebounceRef.current = null;
    }, 300);
    return () => {
      if (equipmentSearchDebounceRef.current) clearTimeout(equipmentSearchDebounceRef.current);
    };
  }, [equipmentSearchQuery, equipmentTypes]);

  // Pre-fill equipment fields from URL query parameters (for WordPress integration)
  useEffect(() => {
    const equipmentName = searchParams.get('equipment_name');
    const equipmentPrice = searchParams.get('equipment_price');
    
    if (equipmentName || equipmentPrice) {
      setForm((prev: any) => {
        const updated: any = { ...prev };
        if (equipmentName) {
          updated.equipment_name = decodeURIComponent(equipmentName);
        }
        if (equipmentPrice) {
          // Parse price: remove $ and commas, keep only digits
          const priceDigits = equipmentPrice.replace(/[^0-9]/g, '');
          updated.equipment_price = priceDigits;
        }
        return updated;
      });
    }
  }, [searchParams]);

  // Sync the ref with the current form state
  useEffect(() => {
    previousOwnersCountRef.current = form.additional_owners_count || 0;
  }, [form.additional_owners_count]);

  // Pre-populate first owner with Step 1 data when navigating to Step 3 if is_business_owner is true
  useEffect(() => {
    if (isCorpOnlySelected) return;
    if (step === 3 && form.is_business_owner === true && form.owners && form.owners.length > 0) {
      const firstOwner = form.owners[0];
      // If first owner is empty or doesn't match Step 1 data, populate it
      if ((!firstOwner.email && !firstOwner.first_name && !firstOwner.last_name) ||
          (firstOwner.email !== form.email || firstOwner.first_name !== form.first_name || firstOwner.last_name !== form.last_name)) {
        const updatedOwners = [...form.owners];
        updatedOwners[0] = {
          ...updatedOwners[0],
          first_name: form.first_name || '',
          last_name: form.last_name || '',
          email: form.email || '',
          phone: form.phone || '',
          same_as_business_address: updatedOwners[0].same_as_business_address || false,
          address_1: updatedOwners[0].address_1 || '',
          address_2: updatedOwners[0].address_2 || '',
          city: updatedOwners[0].city || '',
          state: updatedOwners[0].state || '',
          postal_code: updatedOwners[0].postal_code || '',
          ssn: updatedOwners[0].ssn || '',
          dob: updatedOwners[0].dob || '',
          ownership_percent: updatedOwners[0].ownership_percent || ''
        };
        setForm({ ...form, owners: updatedOwners });
      }
    }
  }, [step, form.is_business_owner, form.owners, form.first_name, form.last_name, form.email, form.phone, isCorpOnlySelected]);

  useEffect(() => {
    if (!commercialLoansAllowsCorpOnly(form.commercial_loans_completed)) {
      if (normalizeSubmissionMode(form.submission_mode) === SUBMISSION_MODE_CORP_ONLY) {
        setForm((prev: any) => ({ ...prev, submission_mode: SUBMISSION_MODE_FULL_GUARANTOR }));
      }
    }
  }, [form.commercial_loans_completed, form.submission_mode]);

  // Sole proprietorship (step 2 commercial): prefill company name from step 1 contact only when company name is blank.
  useEffect(() => {
    if (step !== 2 || !form.is_commercial) return;
    if (form.entity_type !== "Sole Proprietorship") return;
    if (form.company_name?.trim()) return;
    const nm = [form.first_name, form.last_name]
      .map((s) => (s || "").trim())
      .filter(Boolean)
      .join(" ")
      .trim();
    if (!nm) return;
    setForm((prev: any) => (prev.company_name?.trim() ? prev : { ...prev, company_name: nm }));
  }, [step, form.is_commercial, form.entity_type, form.company_name, form.first_name, form.last_name]);

  const EQUIPMENT_TYPES_WITHOUT_MILEAGE_REQUIREMENT = new Set([
    "waste-recycling-environmental-equipment",
    "sanitation-restroom-trailers",
  ]);

  // Validation functions for each step
  const validateStep1 = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!form.first_name?.trim()) errors.first_name = "First name is required";
    if (!form.last_name?.trim()) errors.last_name = "Last name is required";
    if (!form.email?.trim()) errors.email = "Email is required";
    if (!form.phone?.trim() || toDigits(form.phone).length !== 10) {
      errors.phone = "Valid phone number is required (10 digits)";
    }
    return errors;
  };

  const validateStep4Equipment = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!intakeProgram.hide_equipment_name && !form.equipment_name?.trim()) {
      errors.equipment_name = "Equipment name is required";
    }
    if (!form.equipment_price?.trim() || toDigits(form.equipment_price) === "0") {
      errors.equipment_price = "Equipment price is required";
    }
    if (!isEquipmentTypeSelectorLocked && (form.equipment_type_id == null || form.equipment_type_id === "")) {
      errors.equipment_type_id = "Equipment type is required";
    }
    const selectedType = equipmentTypes.find(
      (et: { id: number; slug?: string; underwriting_signals?: string[] }) => et.id === form.equipment_type_id,
    );
    if (selectedType) {
      const signals: string[] = selectedType.underwriting_signals ?? [];
      const programFields = intakeProgram.equipment_fields ?? [];
      if (
        (programFields.includes("year") || signals.includes("year")) &&
        (form.equipment_year === "" || form.equipment_year == null)
      ) {
        errors.equipment_year = "Model year is required for this equipment type";
      }
      if (
        !intakeProgram.hide_equipment_hours &&
        signals.includes("mileage") &&
        !EQUIPMENT_TYPES_WITHOUT_MILEAGE_REQUIREMENT.has(selectedType.slug ?? "") &&
        (form.equipment_mileage === "" || form.equipment_mileage == null)
      ) {
        errors.equipment_mileage = "Mileage is required for this equipment type";
      }
    }
    appendPrequalGateErrors(errors);
    return errors;
  };

  const validateStep2Commercial = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    
    // Company Name: required (comes before Entity Type in UI)
    if (!form.company_name?.trim()) {
      errors.company_name = "Company name is required";
    }
    
    // Entity Type: required (no default)
    if (!form.entity_type?.trim()) {
      errors.entity_type = "Entity type is required";
    }
    
    // Company Name: required if Entity Type is NOT "Sole Proprietorship" (double check)
    if (form.entity_type && form.entity_type !== 'Sole Proprietorship' && !form.company_name?.trim()) {
      errors.company_name = "Company name is required";
    }
    
    // DBA Name: required if "Company has DBA" checkbox is checked
    if (form.has_dba && !form.company_dba?.trim()) {
      errors.company_dba = "DBA name is required when 'Has DBA' is checked";
    }
    
    // Company Address fields: ALL required (address_1, city, state, postal_code)
    if (!form.company_address_1?.trim()) errors.company_address_1 = "Company address is required";
    if (!form.company_city?.trim()) errors.company_city = "Company city is required";
    if (!form.company_state?.trim()) errors.company_state = "Company state is required";
    if (!form.company_postal_code?.trim()) errors.company_postal_code = "Company postal code is required";
    
    // Business Phone: optional, but if provided, must be 10 digits
    if (form.company_phone && form.company_phone.trim()) {
      const phoneDigits = toDigits(form.company_phone);
      if (phoneDigits.length !== 10) {
        errors.company_phone = "Business phone must be 10 digits";
      }
    }
    
    // Tax ID: optional, but if provided, must be 9 digits
    if (form.tax_id && form.tax_id.trim()) {
      const taxIdDigits = toDigits(form.tax_id);
      if (taxIdDigits.length !== 9) {
        errors.tax_id = "Tax ID must be 9 digits if provided";
      }
    }
    
    // Years in Business: required
    if (!form.years_in_business || form.years_in_business === '' || parseInt(form.years_in_business) < 0) {
      errors.years_in_business = "Years in business is required";
    }
    
    // Annual Revenue: required for commercial applications
    if (form.is_commercial) {
      if (!form.annual_revenue || form.annual_revenue === '' || parseFloat(form.annual_revenue) <= 0) {
        errors.annual_revenue = "Annual revenue is required";
      }
    }
    
    // Industry: required
    if (!form.industry?.trim()) {
      errors.industry = "Industry is required";
    }
    
    // Owners Count: required (includes primary, so minimum is 1)
    if (!form.owners_count || form.owners_count < 1) {
      errors.owners_count = "Number of owners is required (minimum 1)";
    }
    
    return errors;
  };

  const appendPrequalGateErrors = (errors: Record<string, string>) => {
    const slug = equipmentTypes.find((e: { id: number; slug?: string }) => e.id === form.equipment_type_id)?.slug;
    const needCdl = Boolean(slug && CDL_EQUIPMENT_SLUGS.has(slug));
    const needFleet = needCdl || form.industry === LOGISTICS_INDUSTRY;
    if (needCdl) {
      if (form.prequal_gate_cdl_held !== "yes" && form.prequal_gate_cdl_held !== "no") {
        errors.prequal_gate_cdl_held = "Please answer the CDL question.";
      }
      if (form.prequal_gate_cdl_held === "yes") {
        if (form.prequal_gate_cdl_over_one_year !== "yes" && form.prequal_gate_cdl_over_one_year !== "no") {
          errors.prequal_gate_cdl_over_one_year = "Please answer how long you have held the CDL.";
        }
      }
      if (form.prequal_gate_first_time_oo !== "yes" && form.prequal_gate_first_time_oo !== "no") {
        errors.prequal_gate_first_time_oo = "Please answer whether you are a first time buyer.";
      }
    }
    if (needFleet) {
      const rawFleet = String(form.prequal_gate_fleet_total || "").trim();
      const fleetDigits = rawFleet.replace(/\D/g, "");
      let fleetTotal = 0;
      let fleetTotalOk = false;
      if (!rawFleet || !fleetDigits) {
        errors.prequal_gate_fleet_total =
          "Enter how many trucks the business operates (use 0 if none).";
      } else {
        fleetTotal = parseInt(fleetDigits, 10);
        if (!Number.isFinite(fleetTotal) || fleetTotal < 0) {
          errors.prequal_gate_fleet_total = "Enter a valid number (0 or more).";
        } else {
          fleetTotalOk = true;
        }
      }
      if (fleetTotalOk && fleetTotal >= 1) {
        if (form.prequal_gate_safer_all !== "yes" && form.prequal_gate_safer_all !== "no") {
          errors.prequal_gate_safer_all = "Please indicate whether all trucks are registered with DOT.";
        }
        if (form.prequal_gate_safer_all === "no") {
          const rawRep = String(form.prequal_gate_safer_reporting ?? "").trim();
          if (rawRep === "") {
            errors.prequal_gate_safer_reporting = "Enter how many trucks are registered with DOT.";
          } else {
            const rep = parseInt(rawRep.replace(/\D/g, "") || "0", 10);
            if (rep < 0 || rep > fleetTotal) {
              errors.prequal_gate_safer_reporting =
                rep > fleetTotal ? "Cannot exceed total trucks operated." : "Enter a valid number (0 or more).";
            }
          }
        }
      }
    }
  };

  const validateStep3 = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (isCorpOnlySelected) {
      if (!form.commercial_loans_completed) {
        errors.commercial_loans_completed =
          "Please answer how many commercial equipment loans you have completed in the past.";
      }
      if (
        !isCorpOnlyEligible(
          form.years_in_business,
          form.entity_type,
          form.commercial_loans_completed,
        )
      ) {
        errors.submission_mode = "This application does not qualify for corporate-only submission.";
      }
      return errors;
    }
    
    // Validate all owners (includes primary + additional)
    if (!form.owners || form.owners.length === 0) {
      errors.owners = "At least one owner is required";
      return errors;
    }
    
    form.owners.forEach((owner: any, idx: number) => {
      if (!owner.first_name?.trim()) errors[`owners.${idx}.first_name`] = "First name is required";
      if (!owner.last_name?.trim()) errors[`owners.${idx}.last_name`] = "Last name is required";
      if (!owner.email?.trim()) errors[`owners.${idx}.email`] = "Email is required";
      if (!owner.phone?.trim() || toDigits(owner.phone).length !== 10) {
        errors[`owners.${idx}.phone`] = "Valid phone is required (10 digits)";
      }
      if (!owner.address_1?.trim()) errors[`owners.${idx}.address_1`] = "Home address is required";
      if (!owner.city?.trim()) errors[`owners.${idx}.city`] = "City is required";
      if (!owner.state?.trim()) errors[`owners.${idx}.state`] = "State is required";
      if (!owner.postal_code?.trim()) errors[`owners.${idx}.postal_code`] = "Postal code is required";
      
      // DOB: required with format validation
      const dobError = validateDOB(owner.dob || '');
      if (dobError) {
        errors[`owners.${idx}.dob`] = dobError;
      }
      
      // SSN: required, must be 9 digits
      if (!owner.ssn?.trim()) {
        errors[`owners.${idx}.ssn`] = "SSN is required";
      } else {
        const ssnDigits = toDigits(owner.ssn);
        if (ssnDigits.length !== 9) {
          errors[`owners.${idx}.ssn`] = "SSN must be 9 digits";
        }
      }
      
      // Ownership %: required (can be zero)
      if (owner.ownership_percent === null || owner.ownership_percent === undefined || owner.ownership_percent === '') {
        errors[`owners.${idx}.ownership_percent`] = "Ownership percentage is required";
      } else {
        const ownershipVal = parseFloat(owner.ownership_percent);
        if (isNaN(ownershipVal) || ownershipVal < 0 || ownershipVal > 100) {
          errors[`owners.${idx}.ownership_percent`] = "Ownership percentage must be between 0 and 100%";
        }
      }
    });
    
    // Validate total ownership percentages don't exceed 100%
    const totalOwnership = form.owners.reduce((sum: number, owner: any) => {
      const val = parseFloat(owner.ownership_percent || '0') || 0;
      return sum + val;
    }, 0);
    
    if (totalOwnership > 100.01) { // Allow small floating point differences
      errors.owners = `Total ownership cannot exceed 100% (currently ${totalOwnership.toFixed(2)}%)`;
      // Also add error to each owner's ownership field for visibility
      form.owners.forEach((owner: any, idx: number) => {
        if (!errors[`owners.${idx}.ownership_percent`]) {
          errors[`owners.${idx}.ownership_percent`] = `Total ownership cannot exceed 100%`;
        }
      });
    }
    
    return errors;
  };

  const stripEquipmentFromPayload = (payload: Record<string, unknown>) => {
    delete payload.equipment_type_id;
    delete payload.equipment_subtype_id;
    delete payload.equipment_name;
    delete payload.equipment_price;
    delete payload.equipment_year;
    delete payload.equipment_mileage;
    delete payload.equipment_hours;
    delete payload.primary_equipment_custom_values;
    delete payload.prequal_gate_answers;
  };

  const buildEquipmentPayload = (): Record<string, unknown> => {
    const pecvDefs = intakeData?.primary_equipment_custom_field_defs ?? [];
    const rawPecv = form.primary_equipment_custom_values || {};
    const pecvPayload: Record<string, string> = {};
    for (const d of pecvDefs) {
      const v = rawPecv[d.key];
      pecvPayload[d.key] = v != null && String(v).trim() ? String(v).trim().slice(0, 144) : "";
    }
    const resolvedEquipmentTypeId = (() => {
      if (form.equipment_type_id != null && form.equipment_type_id !== "") {
        return form.equipment_type_id;
      }
      const slug = intakeProgram.lock_equipment_type_slug;
      if (slug && equipmentTypes.length > 0) {
        const match = equipmentTypes.find((et: { slug?: string }) => et.slug === slug);
        if (match) return match.id;
      }
      if (equipmentTypes.length === 1) {
        return (equipmentTypes[0] as { id: number }).id;
      }
      return null;
    })();
    const resolvedEquipmentName = (
      form.equipment_name?.trim() ||
      intakeProgram.default_equipment_name ||
      ""
    ).trim();
    const payload: Record<string, unknown> = {
      equipment_type_id: resolvedEquipmentTypeId,
      equipment_subtype_id: form.equipment_subtype_id ?? null,
      equipment_name: resolvedEquipmentName,
      equipment_year: form.equipment_year !== "" && form.equipment_year != null ? Number(form.equipment_year) : null,
      equipment_mileage:
        form.equipment_mileage !== "" && form.equipment_mileage != null ? Number(form.equipment_mileage) : null,
      equipment_hours:
        form.equipment_hours !== "" && form.equipment_hours != null ? Number(form.equipment_hours) : null,
    };
    const priceNorm = normalizeCurrencyForSubmit(form.equipment_price);
    if (priceNorm) {
      payload.equipment_price = priceNorm;
    }
    if (pecvDefs.length) {
      payload.primary_equipment_custom_values = pecvPayload;
    }
    const pga = buildPrequalGateAnswersPayload();
    if (pga) payload.prequal_gate_answers = pga;
    return payload;
  };

  // Handler for owners count change with safe comparison
  const handleOwnersCountChange = (newCount: number) => {
    const currentCount = form.additional_owners_count || 0;
    
    // Only update if the count actually changed
    // This prevents issues when selecting text and replacing
    if (newCount === currentCount) {
      return;
    }
    
    // Update the ref to track the previous count for next comparison
    previousOwnersCountRef.current = currentCount;
    
    const arr = [...(form.additional_owners || [])];
    
    // Only remove owners if count is actually decreasing
    if (newCount < currentCount) {
      // Remove from the end
      while (arr.length > newCount) {
        arr.pop();
      }
    } else if (newCount > currentCount) {
      // Add new empty owners
      while (arr.length < newCount) {
        arr.push({ first_name: "", last_name: "", email: "", phone: "", address_1: "", city: "", state: "", postal_code: "", ssn: "", dob: "", ownership_percent: "" });
      }
    }
    
    setForm({ ...form, additional_owners_count: newCount, additional_owners: arr });
    
    // Clear error when changing
    if (fieldErrors.additional_owners_count) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.additional_owners_count;
        return newErrors;
      });
    }
  };

  const buildPrequalGateAnswersPayload = (): Record<string, unknown> | undefined => {
    const out: Record<string, unknown> = {};
    if (showCdlGateQuestions) {
      if (form.prequal_gate_cdl_held === "yes" || form.prequal_gate_cdl_held === "no") {
        out.cdl_held = form.prequal_gate_cdl_held;
      }
      if (form.prequal_gate_cdl_held === "yes") {
        if (form.prequal_gate_cdl_over_one_year === "yes" || form.prequal_gate_cdl_over_one_year === "no") {
          out.cdl_held_over_one_year = form.prequal_gate_cdl_over_one_year;
          out.cdl_held_one_year = form.prequal_gate_cdl_over_one_year;
        }
      } else if (form.prequal_gate_cdl_held === "no") {
        out.cdl_held_over_one_year = "no";
        out.cdl_held_one_year = "no";
      }
      if (form.prequal_gate_first_time_oo === "yes" || form.prequal_gate_first_time_oo === "no") {
        out.first_time_buyer = form.prequal_gate_first_time_oo;
      }
    }
    if (showFleetGateQuestions) {
      const total = parseInt(String(form.prequal_gate_fleet_total).replace(/\D/g, "") || "0", 10);
      if (total < 1) {
        out.safer_fleet_units = 0;
        out.safer_all_reporting = "no";
        out.safer_total_trucks_operated = 0;
      } else {
        if (form.prequal_gate_safer_all === "yes" || form.prequal_gate_safer_all === "no") {
          out.safer_all_reporting = form.prequal_gate_safer_all;
        }
        if (form.prequal_gate_safer_all === "yes") {
          out.safer_fleet_units = total;
          out.safer_total_trucks_operated = total;
        }
        if (form.prequal_gate_safer_all === "no") {
          const raw = String(form.prequal_gate_safer_reporting ?? "").trim();
          const rep = parseInt(raw.replace(/\D/g, "") || "0", 10);
          out.safer_total_trucks_operated = total;
          out.safer_fleet_units = rep;
        }
      }
    }
    return Object.keys(out).length ? out : undefined;
  };

  const applySubmissionModeToPayload = (payload: Record<string, unknown>) => {
    payload.submission_mode = normalizeSubmissionMode(form.submission_mode);
    if (form.commercial_loans_completed) {
      payload.commercial_loans_completed = form.commercial_loans_completed;
    } else {
      delete payload.commercial_loans_completed;
    }
    if (payload.submission_mode === SUBMISSION_MODE_CORP_ONLY) {
      delete payload.owners;
      delete payload.additional_owners;
    }
  };

  // Auto-save on step change
  const saveStep = useMutation({
    mutationFn: async (stepNum: number) => {
      const stepId =
        stepNum === 1
          ? "step_1_personal_info"
          : stepNum === 2
            ? "step_2_commercial"
            : stepNum === 4
              ? "step_4_equipment"
              : "step_3";

      const endpoint = isSalesRepLink
        ? `/api/intake/rep/${token}/save-step/`
        : `/api/intake/invites/${token}/save-step/`;

      if (stepNum === 4) {
        const payload: Record<string, unknown> = {
          step: stepId,
          ...buildEquipmentPayload(),
        };
        if (isSalesRepLink && sessionIdRef.current) {
          payload.session_id = sessionIdRef.current;
        }
        await customInstance.post(endpoint, payload);
        return;
      }

      const payload: any = {
        ...form,
        step: stepId,
        phone: toDigits(form.phone),
        annual_revenue: form.is_commercial ? normalizeCurrencyForSubmit(form.annual_revenue) : undefined,
      };

      stripEquipmentFromPayload(payload);
      
      // Include session_id for sales rep/admin/FI links
      if (isSalesRepLink && sessionIdRef.current) {
        payload.session_id = sessionIdRef.current;
      }
      
      // Explicitly remove owners array when saving step 2 (it's only for step 3)
      // This prevents validation errors from empty owner objects
      if (stepNum !== 3) {
        delete payload.owners;
        delete payload.additional_owners;
      }

      delete payload.requested_amount;
      
      delete payload.prequal_gate_cdl_held;
      delete payload.prequal_gate_cdl_over_one_year;
      delete payload.prequal_gate_first_time_oo;
      delete payload.prequal_gate_fleet_total;
      delete payload.prequal_gate_safer_all;
      delete payload.prequal_gate_safer_reporting;

      applySubmissionModeToPayload(payload);

      // Only include owners array when saving step 3 (standard PG path)
      if (
        stepNum === 3 &&
        normalizeSubmissionMode(form.submission_mode) !== SUBMISSION_MODE_CORP_ONLY
      ) {
        // Filter out incomplete owners (those without email) before sending
        // This matches the backend PartialIntakeSerializer behavior
        const completeOwners = (form.owners || []).filter((o: any) => 
          o.email && o.email.trim()
        );
        
        if (completeOwners.length > 0) {
          payload.owners = completeOwners.map((o: any) => ({
            ...o,
            phone: toDigits(o.phone),
            dob: convertDOBForBackend(o.dob || ''), // Convert MM/DD/YYYY to YYYY-MM-DD
          }));
        } else {
          // If no complete owners, don't send the owners array at all
          delete payload.owners;
        }
        
        // Keep legacy additional_owners for backward compatibility
        const completeAdditionalOwners = (form.additional_owners || []).filter((o: any) => 
          o.email && o.email.trim()
        );
        
        if (completeAdditionalOwners.length > 0) {
          payload.additional_owners = completeAdditionalOwners.map((o: any) => ({
            ...o,
            phone: toDigits(o.phone),
            dob: convertDOBForBackend(o.dob || ''), // Convert MM/DD/YYYY to YYYY-MM-DD
          }));
        } else {
          delete payload.additional_owners;
        }
      }
      
      // Remove empty strings for integer/decimal fields (convert to null/undefined)
      if (payload.years_in_business === '' || payload.years_in_business === null) {
        delete payload.years_in_business;
      } else if (payload.years_in_business) {
        payload.years_in_business = parseInt(payload.years_in_business);
      }
      
      if (payload.ownership_percent === '' || payload.ownership_percent === null) {
        delete payload.ownership_percent;
      } else if (payload.ownership_percent) {
        payload.ownership_percent = parseFloat(payload.ownership_percent);
      }

      payload.is_business_owner = true;

      await customInstance.post(endpoint, payload);
    },
    onError: (error: any) => {
      // Parse backend validation errors and set field-level errors
      if (error?.response?.data) {
        const backendErrors: Record<string, string> = {};
        Object.keys(error.response.data).forEach((key) => {
          const errorMsg = Array.isArray(error.response.data[key]) 
            ? error.response.data[key][0] 
            : error.response.data[key];
          backendErrors[key] = errorMsg;
        });
        setFieldErrors(backendErrors);
      }
    },
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signatureSectionRef = useRef<HTMLDivElement>(null);
  const isDrawingRef = useRef(false);
  const canvasFilledRef = useRef(false);

  const confirmEquipment = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        ...buildEquipmentPayload(),
      };
      if (isSalesRepLink && sessionIdRef.current) {
        payload.session_id = sessionIdRef.current;
      }
      const endpoint = isSalesRepLink
        ? `/api/intake/rep/${token}/confirm-equipment/`
        : `/api/intake/invites/${token}/confirm-equipment/`;
      const { data } = await customInstance.post(endpoint, payload);
      return data;
    },
    onSuccess: (data: { intake_phase?: string }) => {
      toast.success("Equipment selection confirmed.");
      if (data?.intake_phase === "sign") {
        setShowSignatureStep(true);
        if (token) queryClient.invalidateQueries({ queryKey: ["invite", token] });
        return;
      }
      const params = new URLSearchParams({ token: token ?? "" });
      if (isSalesRepLink && sessionIdRef.current) params.set("session_id", sessionIdRef.current);
      navigate(`/thank-you?${params.toString()}`);
    },
    onError: (error: any) => {
      if (error?.response?.data) {
        const backendErrors: Record<string, string> = {};
        const body = error.response.data;
        if (typeof body.error === "string") {
          toast.error(body.error);
          return;
        }
        Object.keys(body).forEach((key) => {
          const raw = body[key];
          const errorMsg = Array.isArray(raw) ? raw[0] : typeof raw === "string" ? raw : String(raw);
          backendErrors[key] = errorMsg;
        });
        setFieldErrors(backendErrors);
        const firstMsg = Object.values(backendErrors)[0];
        toast.error(firstMsg || "Could not confirm equipment selection.");
        return;
      }
      toast.error("Could not confirm equipment selection.");
    },
  });

  const signMutation = useMutation({
    mutationFn: async (signatureDataUrl: string) => {
      const endpoint = isSalesRepLink
        ? `/api/intake/rep/${token}/sign/`
        : `/api/intake/invites/${token}/sign/`;
      const payload: { signature: string; session_id?: string } = { signature: signatureDataUrl };
      if (isSalesRepLink && sessionIdRef.current) payload.session_id = sessionIdRef.current;
      const { data } = await customInstance.post(endpoint, payload);
      return data;
    },
    onSuccess: () => {
      toast.success("Signature recorded.");
      setShowSignatureStep(false);
      if (isStaffManualIntakeInvite) {
        const params = new URLSearchParams({ token: token ?? "" });
        if (isSalesRepLink && sessionIdRef.current) params.set("session_id", sessionIdRef.current);
        navigate(`/thank-you?${params.toString()}`);
      } else {
        setStep(4);
      }
      if (token) queryClient.invalidateQueries({ queryKey: ["invite", token] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to record signature");
    },
  });

  // Scroll to signature area when it appears after "Submit and Sign"
  useEffect(() => {
    if (!showSignatureStep) {
      canvasFilledRef.current = false;
      return;
    }
    if (signMutation.isPending || submitAwaitingSignature) return;
    const canvas = canvasRef.current;
    if (canvas && !canvasFilledRef.current) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      canvasFilledRef.current = true;
    }
    const el = signatureSectionRef.current;
    if (el) {
      const t = setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      }, 150);
      return () => clearTimeout(t);
    }
  }, [showSignatureStep, signMutation.isPending, submitAwaitingSignature]);

  const submit = useMutation({
    mutationFn: async () => {
      // Prepare payload with normalized currency and phone
      const payload: any = {
        ...form,
        phone: toDigits(form.phone),
        company_phone: form.company_phone ? toDigits(form.company_phone) : '',
        annual_revenue: form.is_commercial ? normalizeCurrencyForSubmit(form.annual_revenue) : undefined,
        dob: convertDOBForBackend(form.dob),
      };
      stripEquipmentFromPayload(payload);
      
      // Process owners array - normalize phone and DOB for each owner
      if (normalizeSubmissionMode(form.submission_mode) !== SUBMISSION_MODE_CORP_ONLY) {
      if (form.owners && Array.isArray(form.owners) && form.owners.length > 0) {
        const completeOwners = form.owners.filter((o: any) => 
          o.email && o.email.trim() && 
          o.first_name && o.first_name.trim() && 
          o.last_name && o.last_name.trim()
        );
        
        if (completeOwners.length > 0) {
          payload.owners = completeOwners.map((o: any) => ({
            ...o,
              phone: toDigits(o.phone || ""),
              dob: convertDOBForBackend(o.dob || ""),
          }));
        }
      }
      
      if (form.additional_owners && Array.isArray(form.additional_owners) && form.additional_owners.length > 0) {
        const completeAdditionalOwners = form.additional_owners.filter((o: any) => 
          o.email && o.email.trim() && 
          o.first_name && o.first_name.trim() && 
          o.last_name && o.last_name.trim()
        );
        
        if (completeAdditionalOwners.length > 0) {
          payload.additional_owners = completeAdditionalOwners.map((o: any) => ({
            ...o,
              phone: toDigits(o.phone || ""),
              dob: convertDOBForBackend(o.dob || ""),
          }));
          }
        }
      }
      
      // Include session_id for sales rep/admin/FI links
      if (isSalesRepLink && sessionIdRef.current) {
        payload.session_id = sessionIdRef.current;
      }

      // Requested amount is not collected in the application flow; set later in the portal
      delete payload.requested_amount;

      delete payload.prequal_gate_cdl_held;
      delete payload.prequal_gate_cdl_over_one_year;
      delete payload.prequal_gate_first_time_oo;
      delete payload.prequal_gate_fleet_total;
      delete payload.prequal_gate_safer_all;
      delete payload.prequal_gate_safer_reporting;

      applySubmissionModeToPayload(payload);

      payload.is_business_owner = true;

      const endpoint = isSalesRepLink
        ? `/api/intake/rep/${token}/submit/`
        : `/api/intake/invites/${token}/submit/`;
      const response = await customInstance.post(endpoint, payload);
      const data = response.data || response;
      return data;
    },
    onSuccess: () => {
      toast.success(
        isStaffManualIntakeInvite
          ? "Application submitted."
          : "Application submitted. Please sign below.",
      );
      if (isStaffManualIntakeInvite) {
        setStep(4);
        setShowSignatureStep(false);
      } else {
        setShowSignatureStep(true);
      }
      setSubmitAwaitingSignature(false);
      if (token) queryClient.invalidateQueries({ queryKey: ["invite", token] });
    },
    onError: (error: any) => {
      
      // Parse backend validation errors and set field-level errors
      if (error?.response?.data) {
        console.error("Validation errors:", error.response.data);
        const backendErrors: Record<string, string> = {};
        Object.keys(error.response.data).forEach((key) => {
          const errorMsg = Array.isArray(error.response.data[key]) 
            ? error.response.data[key][0] 
            : error.response.data[key];
          backendErrors[key] = errorMsg;
        });
        setFieldErrors(backendErrors);
      }
    },
  });

  return (
    <div
      ref={formContainerRef}
      className={`max-w-3xl mx-auto p-6 space-y-6 ${isInIframe ? 'min-h-screen flex flex-col justify-center' : ''}`}
    >
      <div className="space-y-6 flex-1">
      <div className="flex justify-center mb-6 px-2 min-h-[8rem] items-center">
        {organizationLogoUrl ? (
          <img
            src={organizationLogoUrl}
            alt="Organization logo"
            className="max-h-48 max-w-lg w-auto object-contain"
          />
        ) : brandingPlaceholder ? (
          <div
            className="max-h-48 max-w-lg w-full min-h-[7rem] rounded-lg bg-muted animate-pulse"
            aria-busy="true"
            aria-label="Loading branding"
          />
        ) : (
          <img
            src="/logo-full.png"
            alt="DealerDog"
            className="max-h-48 max-w-lg w-auto object-contain"
          />
        )}
      </div>

      {step === 1 && !showSignatureStep && (
        <div className="space-y-4 bg-white rounded-xl border p-4">
          <p className="text-xs text-muted-foreground">Step 1 of 4</p>
          {isStaffManualIntakeInvite && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Imported or staff-entered application — check the details below, then continue and submit.
            </p>
          )}
          <h1 className="text-2xl font-bold text-center">Buy with confidence. Put it to work.</h1>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>First Name</Label>
              <Input 
                required 
                value={form.first_name} 
                onChange={(e) => {
                  setForm({ ...form, first_name: e.target.value });
                  if (fieldErrors.first_name) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.first_name;
                      return newErrors;
                    });
                  }
                }}
                className={fieldErrors.first_name ? "border-red-500" : ""}
              />
              {fieldErrors.first_name && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.first_name}</p>
              )}
            </div>
            <div>
              <Label>Last Name</Label>
              <Input 
                required 
                value={form.last_name} 
                onChange={(e) => {
                  setForm({ ...form, last_name: e.target.value });
                  if (fieldErrors.last_name) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.last_name;
                      return newErrors;
                    });
                  }
                }}
                className={fieldErrors.last_name ? "border-red-500" : ""}
              />
              {fieldErrors.last_name && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.last_name}</p>
              )}
            </div>
            <div>
              <Label>Email</Label>
              <Input 
                required 
                type="email" 
                value={form.email} 
                onChange={(e) => {
                  setForm({ ...form, email: e.target.value });
                  if (fieldErrors.email) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.email;
                      return newErrors;
                    });
                  }
                }}
                className={fieldErrors.email ? "border-red-500" : ""}
              />
              {fieldErrors.email && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.email}</p>
              )}
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                required
                value={form.phone}
                onChange={(e) => handlePhoneChange(e.target.value, 'phone', (val) => setForm({ ...form, phone: val }))}
                placeholder="555-555-5555"
                maxLength={12}
                className={fieldErrors.phone ? "border-red-500" : ""}
              />
              {fieldErrors.phone && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.phone}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <Button 
              onClick={async () => {
                const errors = validateStep1();
                console.log("Validation errors:", errors);
                if (Object.keys(errors).length > 0) {
                  console.log("Setting field errors:", errors);
                  setFieldErrors(errors);
                  // Scroll to first error
                  setTimeout(() => scrollToFirstError(), 100);
                  return;
                }
                setFieldErrors({});
                const prevStep = step;
                    setStep(2);
                saveStep.mutate(1, {
                  onError: () => {
                    setStep(prevStep);
                    toast.error("Could not save your progress. Please try again.");
                  },
                });
              }} 
              className="bg-blue-600 text-white"
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {step === 2 && form.is_commercial && !showSignatureStep && (
        <div className="space-y-4 bg-white rounded-xl border p-4">
          <p className="text-xs text-muted-foreground">Step 2 of 4</p>
          <h1 className="text-2xl font-bold text-center">Tell us about your business.</h1>
          <p className="text-center text-sm text-muted-foreground">This step takes about a minute.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Entity Type</Label>
              <select
                required
                className={`w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.entity_type ? "border-red-500" : ""}`}
                value={form.entity_type}
                onChange={(e) => {
                  const next = e.target.value;
                  let company_name = form.company_name;
                  if (next === "Sole Proprietorship" && !form.company_name?.trim()) {
                    const nm = [form.first_name, form.last_name]
                      .map((s) => (s || "").trim())
                      .filter(Boolean)
                      .join(" ")
                      .trim();
                    if (nm) company_name = nm;
                  }
                  setForm({ ...form, entity_type: next, company_name });
                  if (fieldErrors.entity_type) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.entity_type;
                      return newErrors;
                    });
                  }
                }}
              >
                <option value="">Select Entity Type</option>
                {[
                  "LLC",
                  "Corporation",
                  "S-Corp",
                  "Partnership",
                  "Sole Proprietorship",
                  "Non-Profit",
                ].map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              {fieldErrors.entity_type && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.entity_type}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <Label>Company Name</Label>
              <Input 
                required
                value={form.company_name} 
                onChange={(e) => {
                  setForm({ ...form, company_name: e.target.value });
                  if (fieldErrors.company_name) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.company_name;
                      return newErrors;
                    });
                  }
                }}
                className={fieldErrors.company_name ? "border-red-500" : ""}
              />
              {fieldErrors.company_name && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.company_name}</p>
              )}
            </div>
            <div className="md:col-span-2 flex items-center gap-3">
              <input
                id="has_dba"
                type="checkbox"
                checked={form.has_dba}
                onChange={(e) => setForm({ ...form, has_dba: e.target.checked, company_dba: e.target.checked ? form.company_dba : '' })}
              />
              <Label htmlFor="has_dba">Company has DBA (Doing Business As)</Label>
            </div>
            {form.has_dba && (
              <div className="md:col-span-2">
                <Label>DBA Name</Label>
                <Input 
                  value={form.company_dba} 
                  onChange={(e) => setForm({ ...form, company_dba: e.target.value })} 
                  className={fieldErrors.company_dba ? "border-red-500" : ""}
                />
                {fieldErrors.company_dba && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors.company_dba}</p>
                )}
              </div>
            )}
            <div className="md:col-span-2">
              <Label>Company Address 1</Label>
              <Input 
                required 
                value={form.company_address_1} 
                onChange={(e) => {
                  setForm({ ...form, company_address_1: e.target.value });
                  if (fieldErrors.company_address_1) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.company_address_1;
                      return newErrors;
                    });
                  }
                }}
                className={fieldErrors.company_address_1 ? "border-red-500" : ""}
              />
              {fieldErrors.company_address_1 && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.company_address_1}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <Label>Company Address 2</Label>
              <Input value={form.company_address_2} onChange={(e) => setForm({ ...form, company_address_2: e.target.value })} />
            </div>
            <div>
              <Label>Company City</Label>
              <Input 
                required 
                value={form.company_city} 
                onChange={(e) => {
                  setForm({ ...form, company_city: e.target.value });
                  if (fieldErrors.company_city) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.company_city;
                      return newErrors;
                    });
                  }
                }}
                className={fieldErrors.company_city ? "border-red-500" : ""}
              />
              {fieldErrors.company_city && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.company_city}</p>
              )}
            </div>
            <div>
              <Label>Company State</Label>
              <select
                required
                className={`w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.company_state ? "border-red-500" : ""}`}
                value={form.company_state}
                onChange={(e) => {
                  setForm({ ...form, company_state: e.target.value });
                  if (fieldErrors.company_state) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.company_state;
                      return newErrors;
                    });
                  }
                }}
              >
                <option value="">Select State</option>
                {US_STATES.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </select>
              {fieldErrors.company_state && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.company_state}</p>
              )}
            </div>
            <div>
              <Label>Company Postal Code</Label>
              <Input 
                required 
                value={form.company_postal_code} 
                onChange={(e) => {
                  setForm({ ...form, company_postal_code: e.target.value });
                  if (fieldErrors.company_postal_code) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.company_postal_code;
                      return newErrors;
                    });
                  }
                }}
                className={fieldErrors.company_postal_code ? "border-red-500" : ""}
              />
              {fieldErrors.company_postal_code && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.company_postal_code}</p>
              )}
            </div>
            <div>
              <Label>Business Phone Number (optional)</Label>
              <Input
                type="text"
                value={form.company_phone}
                onChange={(e) => handlePhoneChange(e.target.value, 'company_phone', (val) => setForm({ ...form, company_phone: val }))}
                placeholder="xxx-xxx-xxxx"
                maxLength={14}
                className={fieldErrors.company_phone ? "border-red-500" : ""}
              />
              {fieldErrors.company_phone && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.company_phone}</p>
              )}
            </div>
            <div>
              <Label>Tax ID (optional)</Label>
              <Input 
                value={form.tax_id} 
                onChange={(e) => handleTaxIdChange(e.target.value, (val) => setForm({ ...form, tax_id: val }))}
                placeholder="12-3456789"
                maxLength={10}
                className={fieldErrors.tax_id ? "border-red-500" : ""}
              />
              {fieldErrors.tax_id && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.tax_id}</p>
              )}
            </div>
            <div>
              <Label>Years in Business</Label>
              <Input 
                required 
                type="number"
                min="0"
                value={form.years_in_business} 
                onWheel={(e) => {
                  e.currentTarget.blur();
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onChange={(e) => {
                  const val = e.target.value;
                  // Only allow digits
                  if (val === '' || /^\d+$/.test(val)) {
                    setForm({ ...form, years_in_business: val });
                    if (fieldErrors.years_in_business) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.years_in_business;
                        return newErrors;
                      });
                    }
                  }
                }}
                className={fieldErrors.years_in_business ? "border-red-500" : ""}
              />
              {fieldErrors.years_in_business && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.years_in_business}</p>
              )}
            </div>
            <div>
              <Label>Industry</Label>
              <select
                required
                disabled={Boolean(intakeProgram.lock_industry)}
                className={`w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 ${fieldErrors.industry ? "border-red-500" : ""}`}
                value={form.industry}
                onChange={(e) => {
                  setForm({ ...form, industry: e.target.value });
                  if (fieldErrors.industry) {
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.industry;
                      return next;
                    });
                  }
                }}
              >
                <option value="">Select industry</option>
                {(intakeData?.industries || []).map((ind) => (
                  <option key={ind.id} value={ind.name}>{ind.name}</option>
                ))}
              </select>
              {fieldErrors.industry && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.industry}</p>
              )}
            </div>
            <div>
              <Label>Annual Revenue ($)</Label>
              <Input
                value={formatCurrency(form.annual_revenue)}
                onChange={(e) => {
                  // Extract digits only to store in form state
                  const digits = toDigits(e.target.value);
                  setForm({ ...form, annual_revenue: digits });
                  // Clear error when typing
                  if (fieldErrors.annual_revenue) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.annual_revenue;
                      return newErrors;
                    });
                  }
                }}
                placeholder="Enter amount"
                className={fieldErrors.annual_revenue ? "border-red-500" : ""}
                required
              />
              {fieldErrors.annual_revenue && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.annual_revenue}</p>
              )}
            </div>
            <div>
              <Label>How many owners own more than 20%? *</Label>
              <Input
                type="number"
                min="1"
                max="10"
                required
                value={form.owners_count ?? ''}
                onWheel={(e) => {
                  e.currentTarget.blur();
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onChange={(e) => {
                  const inputVal = e.target.value;
                  if (inputVal === '') {
                    setForm({ ...form, owners_count: undefined });
                    // Clear error when clearing
                    if (fieldErrors.owners_count) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.owners_count;
                        return newErrors;
                      });
                    }
                    return;
                  }
                  const val = parseInt(inputVal) || 1;
                  setForm({ ...form, owners_count: val });
                  // Auto-update owners array length
                  const currentOwners = form.owners || [];
                  if (val > currentOwners.length) {
                    // Add new empty owners
                    const newOwners = [...currentOwners];
                    while (newOwners.length < val) {
                      newOwners.push({
                        first_name: "",
                        last_name: "",
                        email: "",
                        phone: "",
                        same_as_business_address: false,
                        address_1: "",
                        address_2: "",
                        city: "",
                        state: "",
                        postal_code: "",
                        ssn: "",
                        dob: "",
                        ownership_percent: ""
                      });
                    }
                    setForm({ ...form, owners_count: val, owners: newOwners });
                  } else if (val < currentOwners.length) {
                    // Remove from the end
                    const newOwners = currentOwners.slice(0, val);
                    setForm({ ...form, owners_count: val, owners: newOwners });
                  } else {
                    setForm({ ...form, owners_count: val });
                  }
                  // Clear error when typing
                  if (fieldErrors.owners_count) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.owners_count;
                      return newErrors;
                    });
                  }
                }}
                placeholder=""
                className={fieldErrors.owners_count ? "border-red-500" : ""}
              />
              {fieldErrors.owners_count && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.owners_count}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Includes all owners with more than 20% ownership (including yourself if you're an owner)</p>
            </div>
          </div>
          <div className="flex justify-between">
            <Button 
              variant="secondary" 
              onClick={() => {
                // Don't save when going back — saving step 2 here would set last_completed_step
                // and the hydrate effect would skip the user forward past Continue.
                setStep(1);
              }}
            >
              Back
            </Button>
            <Button 
              onClick={async () => {
                const errors = validateStep2Commercial();
                if (Object.keys(errors).length > 0) {
                  setFieldErrors(errors);
                  // Scroll to first error
                  setTimeout(() => scrollToFirstError(), 100);
                  return;
                }
                setFieldErrors({});
                const prevStep = step;
                  setStep(3);
                saveStep.mutate(2, {
                  onError: () => {
                    setStep(prevStep);
                    toast.error("Could not save your progress. Please try again.");
                  },
                });
              }} 
              className="bg-blue-600 text-white"
              disabled={saveStep.isPending}
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {step === 3 && !showSignatureStep && (
        <div className="space-y-4 bg-white rounded-xl border p-4">
          {!showSignatureStep && (
            <p className="text-xs text-muted-foreground">Step 3 of 4</p>
          )}
          {isCorpOnlySelected ? (
            <p className="text-sm text-muted-foreground">
              Corporate-only applications are reviewed manually. No personal credit check at this stage.
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Soft credit check. No impact to your credit.
              </p>
              <ul className="flex flex-col gap-1.5 text-sm">
                {["Quick prequalification", "No obligation, no fees"].map((text) => (
                  <li key={text} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" aria-hidden />
                    <span className="text-gray-800">{text}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {showCommercialLoansQuestion && (
            <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-4 space-y-3">
              <Label className="text-sm font-medium text-gray-900">
                How many commercial equipment loans have you completed in the past? *
              </Label>
              <div className="flex flex-wrap gap-2">
                {COMMERCIAL_LOANS_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`inline-flex items-center rounded-md border px-3 py-2 text-sm cursor-pointer ${
                      form.commercial_loans_completed === opt.value
                        ? "border-blue-600 bg-blue-50 text-blue-900"
                        : "border-gray-300 bg-white hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="commercial_loans_completed"
                      className="sr-only"
                      checked={form.commercial_loans_completed === opt.value}
                      onChange={() => {
                        setForm((prev: any) => ({
                          ...prev,
                          commercial_loans_completed: opt.value,
                        }));
                        if (fieldErrors.commercial_loans_completed) {
                          setFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.commercial_loans_completed;
                            return next;
                          });
                        }
                      }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
              {fieldErrors.commercial_loans_completed && (
                <p className="text-red-500 text-sm">{fieldErrors.commercial_loans_completed}</p>
              )}
            </div>
          )}

          {showCorpOnlyFork && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-4 space-y-3">
              <Label className="text-sm font-medium text-gray-900">Application type *</Label>
              <div className="space-y-2">
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="submission_mode"
                    className="mt-1"
                    checked={!isCorpOnlySelected}
                    onChange={() => {
                      setForm((prev: any) => ({
                        ...prev,
                        submission_mode: SUBMISSION_MODE_FULL_GUARANTOR,
                      }));
                      if (fieldErrors.submission_mode) {
                        setFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next.submission_mode;
                          return next;
                        });
                      }
                    }}
                  />
                  <span>
                    <span className="font-medium text-gray-900">Standard application (personal guarantor)</span>
                    <span className="block text-gray-600 text-xs mt-0.5">
                      Includes owner information and a soft credit check.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="submission_mode"
                    className="mt-1"
                    checked={isCorpOnlySelected}
                    onChange={() => {
                      setForm((prev: any) => ({
                        ...prev,
                        submission_mode: SUBMISSION_MODE_CORP_ONLY,
                      }));
                      if (fieldErrors.submission_mode) {
                        setFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next.submission_mode;
                          return next;
                        });
                      }
                    }}
                  />
                  <span>
                    <span className="font-medium text-gray-900">Corporate-only application</span>
                    <span className="block text-gray-600 text-xs mt-0.5">
                      Authorized signer only — no personal guarantor at this stage. Reviewed manually.
                    </span>
                  </span>
                </label>
              </div>
              {fieldErrors.submission_mode && (
                <p className="text-red-500 text-sm">{fieldErrors.submission_mode}</p>
              )}
            </div>
          )}

          {!isCorpOnlySelected && (
            <>
          <h2 className="text-xl font-semibold mb-4">Owner Information</h2>
          <p className="text-sm text-gray-600 mb-4">
            Please provide information for all owners with more than 20% ownership.
          </p>
            </>
          )}

          {isCorpOnlySelected && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-2 mb-2">
              <h2 className="text-lg font-semibold text-gray-900">Authorized signer</h2>
              <p className="text-sm text-gray-600">
                Your Step 1 contact will sign as the authorized company representative.
              </p>
              <p className="text-sm font-medium text-gray-900">
                {[form.first_name, form.last_name].filter(Boolean).join(" ") || "—"}
              </p>
              <p className="text-sm text-gray-700">{form.email || "—"}</p>
              <p className="text-sm text-gray-700">{form.phone || "—"}</p>
            </div>
          )}

          
          {!isCorpOnlySelected && (form.owners || []).map((owner: any, idx: number) => {
            // Check if this is the Step 1 person (if they're an owner)
            // Only lock if is_business_owner is true AND this is the first owner (idx === 0)
            // We check if email matches OR if it's empty (not yet populated) - both indicate Step 1 person
            const isStep1Person = form.is_business_owner === true && idx === 0 && (
              (owner.email === form.email && owner.first_name === form.first_name && owner.last_name === form.last_name) ||
              (!owner.email && !owner.first_name && !owner.last_name) // Empty owner slot = will be Step 1 person
            );
            
            return (
              <div key={idx} className="p-4 border-2 border-gray-200 rounded-lg bg-gray-50 mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  {idx === 0 ? 'Primary Owner' : `Owner ${idx + 1}`}
                  {isStep1Person && (
                    <span className="ml-2 text-xs text-gray-500">
                      (Information provided in Step 1)
                    </span>
                  )}
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>First Name {isStep1Person && <span className="text-gray-400">(Locked)</span>}</Label>
                    <Input 
                      required
                      value={owner.first_name || ''} 
                      onChange={(e) => {
                        if (isStep1Person) return; // Locked
                        const arr = [...(form.owners || [])]; 
                        arr[idx].first_name = e.target.value; 
                        setForm({ ...form, owners: arr });
                        if (fieldErrors[`owners.${idx}.first_name`]) {
                          setFieldErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[`owners.${idx}.first_name`];
                            return newErrors;
                          });
                        }
                      }}
                      disabled={isStep1Person}
                      className={`${isStep1Person ? 'bg-gray-100 cursor-not-allowed' : ''} ${fieldErrors[`owners.${idx}.first_name`] ? "border-red-500" : ""}`}
                    />
                    {fieldErrors[`owners.${idx}.first_name`] && (
                      <p className="text-red-500 text-sm mt-1">{fieldErrors[`owners.${idx}.first_name`]}</p>
                    )}
                  </div>
                  <div>
                    <Label>Last Name {isStep1Person && <span className="text-gray-400">(Locked)</span>}</Label>
                    <Input 
                      required
                      value={owner.last_name || ''} 
                      onChange={(e) => {
                        if (isStep1Person) return; // Locked
                        const arr = [...(form.owners || [])]; 
                        arr[idx].last_name = e.target.value; 
                        setForm({ ...form, owners: arr });
                        if (fieldErrors[`owners.${idx}.last_name`]) {
                          setFieldErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[`owners.${idx}.last_name`];
                            return newErrors;
                          });
                        }
                      }}
                      disabled={isStep1Person}
                      className={`${isStep1Person ? 'bg-gray-100 cursor-not-allowed' : ''} ${fieldErrors[`owners.${idx}.last_name`] ? "border-red-500" : ""}`}
                    />
                    {fieldErrors[`owners.${idx}.last_name`] && (
                      <p className="text-red-500 text-sm mt-1">{fieldErrors[`owners.${idx}.last_name`]}</p>
                    )}
                  </div>
                  <div>
                    <Label>Email {isStep1Person && <span className="text-gray-400">(Locked)</span>}</Label>
                    <Input 
                      required
                      type="email" 
                      value={owner.email || ''} 
                      onChange={(e) => {
                        if (isStep1Person) return; // Locked
                        const arr = [...(form.owners || [])]; 
                        arr[idx].email = e.target.value; 
                        setForm({ ...form, owners: arr });
                        if (fieldErrors[`owners.${idx}.email`]) {
                          setFieldErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[`owners.${idx}.email`];
                            return newErrors;
                          });
                        }
                      }}
                      disabled={isStep1Person}
                      className={`${isStep1Person ? 'bg-gray-100 cursor-not-allowed' : ''} ${fieldErrors[`owners.${idx}.email`] ? "border-red-500" : ""}`}
                    />
                    {fieldErrors[`owners.${idx}.email`] && (
                      <p className="text-red-500 text-sm mt-1">{fieldErrors[`owners.${idx}.email`]}</p>
                    )}
                  </div>
                  <div>
                    <Label>Phone {isStep1Person && <span className="text-gray-400">(Locked)</span>}</Label>
                    <Input 
                      required
                      value={owner.phone || ''} 
                      onChange={(e) => {
                        if (isStep1Person) return; // Locked
                        const arr = [...(form.owners || [])];
                        handlePhoneChange(e.target.value, `owners.${idx}.phone`, (val) => {
                          arr[idx].phone = val;
                          setForm({ ...form, owners: arr });
                        });
                      }}
                      disabled={isStep1Person}
                      placeholder="555-555-5555"
                      maxLength={12}
                      className={`${isStep1Person ? 'bg-gray-100 cursor-not-allowed' : ''} ${fieldErrors[`owners.${idx}.phone`] ? "border-red-500" : ""}`}
                    />
                    {fieldErrors[`owners.${idx}.phone`] && (
                      <p className="text-red-500 text-sm mt-1">{fieldErrors[`owners.${idx}.phone`]}</p>
                    )}
                  </div>
                  
                  <div className="md:col-span-2 flex items-center gap-3">
                    <input
                      id={`same_as_business_${idx}`}
                      type="checkbox"
                      checked={owner.same_as_business_address || false}
                      onChange={(e) => {
                        const arr = [...(form.owners || [])];
                        arr[idx].same_as_business_address = e.target.checked;
                        if (e.target.checked && form.company_address_1) {
                          // Sync address from company
                          arr[idx].address_1 = form.company_address_1;
                          arr[idx].address_2 = form.company_address_2 || '';
                          arr[idx].city = form.company_city;
                          arr[idx].state = form.company_state;
                          arr[idx].postal_code = form.company_postal_code;
                        }
                        setForm({ ...form, owners: arr });
                      }}
                    />
                    <Label htmlFor={`same_as_business_${idx}`}>Home Address matches Business Address</Label>
                  </div>
                  
                  {!owner.same_as_business_address && (
                    <>
                      <div className="md:col-span-2">
                        <Label>Home Address 1 *</Label>
                        <Input 
                          required
                          value={owner.address_1 || ''} 
                          onChange={(e) => {
                            const arr = [...(form.owners || [])]; 
                            arr[idx].address_1 = e.target.value; 
                            setForm({ ...form, owners: arr });
                            if (fieldErrors[`owners.${idx}.address_1`]) {
                              setFieldErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[`owners.${idx}.address_1`];
                                return newErrors;
                              });
                            }
                          }}
                          className={fieldErrors[`owners.${idx}.address_1`] ? "border-red-500" : ""}
                        />
                        {fieldErrors[`owners.${idx}.address_1`] && (
                          <p className="text-red-500 text-sm mt-1">{fieldErrors[`owners.${idx}.address_1`]}</p>
                        )}
                      </div>
                      <div className="md:col-span-2">
                        <Label>Home Address 2</Label>
                        <Input 
                          value={owner.address_2 || ''} 
                          onChange={(e) => {
                            const arr = [...(form.owners || [])]; 
                            arr[idx].address_2 = e.target.value; 
                            setForm({ ...form, owners: arr });
                          }}
                        />
                      </div>
                      <div>
                        <Label>City *</Label>
                        <Input 
                          required
                          value={owner.city || ''} 
                          onChange={(e) => {
                            const arr = [...(form.owners || [])]; 
                            arr[idx].city = e.target.value; 
                            setForm({ ...form, owners: arr });
                            if (fieldErrors[`owners.${idx}.city`]) {
                              setFieldErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[`owners.${idx}.city`];
                                return newErrors;
                              });
                            }
                          }}
                          className={fieldErrors[`owners.${idx}.city`] ? "border-red-500" : ""}
                        />
                        {fieldErrors[`owners.${idx}.city`] && (
                          <p className="text-red-500 text-sm mt-1">{fieldErrors[`owners.${idx}.city`]}</p>
                        )}
                      </div>
                      <div>
                        <Label>State *</Label>
                        <select
                          required
                          className={`w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors[`owners.${idx}.state`] ? "border-red-500" : ""}`}
                          value={owner.state || ''}
                          onChange={(e) => {
                            const arr = [...(form.owners || [])];
                            arr[idx].state = e.target.value;
                            setForm({ ...form, owners: arr });
                            if (fieldErrors[`owners.${idx}.state`]) {
                              setFieldErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[`owners.${idx}.state`];
                                return newErrors;
                              });
                            }
                          }}
                        >
                          <option value="">Select State</option>
                          {US_STATES.map((state) => (
                            <option key={state.value} value={state.value}>
                              {state.label}
                            </option>
                          ))}
                        </select>
                        {fieldErrors[`owners.${idx}.state`] && (
                          <p className="text-red-500 text-sm mt-1">{fieldErrors[`owners.${idx}.state`]}</p>
                        )}
                      </div>
                      <div>
                        <Label>Postal Code *</Label>
                        <Input 
                          required
                          value={owner.postal_code || ''} 
                          onChange={(e) => {
                            const arr = [...(form.owners || [])]; 
                            arr[idx].postal_code = e.target.value; 
                            setForm({ ...form, owners: arr });
                            if (fieldErrors[`owners.${idx}.postal_code`]) {
                              setFieldErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[`owners.${idx}.postal_code`];
                                return newErrors;
                              });
                            }
                          }}
                          className={fieldErrors[`owners.${idx}.postal_code`] ? "border-red-500" : ""}
                        />
                        {fieldErrors[`owners.${idx}.postal_code`] && (
                          <p className="text-red-500 text-sm mt-1">{fieldErrors[`owners.${idx}.postal_code`]}</p>
                        )}
                      </div>
                    </>
                  )}
                  
                  <div>
                    <Label>Date of Birth (MM/DD/YYYY) *</Label>
                    <Input 
                      required
                      placeholder="MM/DD/YYYY" 
                      value={owner.dob || ''} 
                      maxLength={10}
                      onChange={(e) => {
                        const arr = [...(form.owners || [])];
                        handleDOBChange(e.target.value, (val) => {
                          arr[idx].dob = val;
                          setForm({ ...form, owners: arr });
                        }, `owners.${idx}.dob`);
                      }}
                      className={fieldErrors[`owners.${idx}.dob`] ? "border-red-500" : ""}
                    />
                    {fieldErrors[`owners.${idx}.dob`] && (
                      <p className="text-red-500 text-sm mt-1">{fieldErrors[`owners.${idx}.dob`]}</p>
                    )}
                  </div>
                  <div>
                    <Label>SSN *</Label>
                    <Input 
                      required
                      value={owner.ssn || ''} 
                      onChange={(e) => {
                        const arr = [...(form.owners || [])];
                        const digits = toDigits(e.target.value);
                        const limited = digits.slice(0, 9);
                        const formatted = limited.length > 5
                          ? `${limited.slice(0, 3)}-${limited.slice(3, 5)}-${limited.slice(5)}`
                          : limited.length > 3
                          ? `${limited.slice(0, 3)}-${limited.slice(3)}`
                          : limited;
                        arr[idx].ssn = formatted;
                        setForm({ ...form, owners: arr });
                        if (fieldErrors[`owners.${idx}.ssn`]) {
                          setFieldErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[`owners.${idx}.ssn`];
                            return newErrors;
                          });
                        }
                      }}
                      placeholder="123-45-6789"
                      maxLength={11}
                      className={fieldErrors[`owners.${idx}.ssn`] ? "border-red-500" : ""}
                    />
                    {fieldErrors[`owners.${idx}.ssn`] && (
                      <p className="text-red-500 text-sm mt-1">{fieldErrors[`owners.${idx}.ssn`]}</p>
                    )}
                  </div>
                  <div>
                    <Label>Ownership % *</Label>
                    <Input 
                      required
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      onWheel={(e) => {
                        e.currentTarget.blur();
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      value={owner.ownership_percent || ''} 
                      onChange={(e) => { 
                        const arr = [...(form.owners || [])]; 
                        arr[idx].ownership_percent = e.target.value; 
                        setForm({ ...form, owners: arr });
                        if (fieldErrors[`owners.${idx}.ownership_percent`]) {
                          setFieldErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[`owners.${idx}.ownership_percent`];
                            return newErrors;
                          });
                        }
                      }}
                      className={fieldErrors[`owners.${idx}.ownership_percent`] ? "border-red-500" : ""}
                    />
                    {fieldErrors[`owners.${idx}.ownership_percent`] && (
                      <p className="text-red-500 text-sm mt-1">{fieldErrors[`owners.${idx}.ownership_percent`]}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          
          <div className="flex justify-between">
            <Button 
              variant="secondary" 
              onClick={() => {
                // Don't save when going back - just navigate
                setStep(2);
              }}
            >
              Back
            </Button>
            <Button 
              onClick={async () => {
                const errors = validateStep3();
                if (Object.keys(errors).length > 0) {
                  setFieldErrors(errors);
                  // Scroll to first error
                  setTimeout(() => scrollToFirstError(), 100);
                  return;
                }
                setFieldErrors({});
                setSubmitAwaitingSignature(true);
                try {
                  await submit.mutateAsync();
                } catch (error) {
                  setSubmitAwaitingSignature(false);
                  console.error("Failed to submit form:", error);
                }
              }}
              className="bg-blue-600 text-white"
              disabled={
                saveStep.isPending ||
                submit.isPending ||
                submitAwaitingSignature ||
                (!isSalesRepLink && !!token && inviteQueryPending)
              }
            >
              {submit.isPending
                ? "Submitting..."
                : isStaffManualIntakeInvite
                  ? "Submit application"
                  : "Submit and Sign"}
            </Button>
          </div>
        </div>
      )}

      {step === 4 && !showSignatureStep && (
        <div className="space-y-4 bg-white rounded-xl border p-4">
          <p className="text-xs text-muted-foreground">Step 4 of 4</p>
          <h1 className="text-2xl font-bold text-center">
            {isDemcoGrainCart ? "Demco Grain Cart" : "Select your equipment"}
          </h1>
          <p className="text-center text-sm text-muted-foreground">
            {isDemcoGrainCart
              ? "Enter the model year and price for your grain cart."
              : "Confirm the equipment for this application."}
          </p>

          {inviteData?.saved_data?.equipments?.length > 0 && !isDemcoGrainCart && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mb-4">
              <p className="text-sm font-medium text-gray-800 mb-1">You&apos;re looking at:</p>
              <p className="text-sm text-gray-600">
                {(inviteData?.saved_data?.equipments ?? []).map((eq: any) => eq.name || `Equipment #${eq.id}`).join(", ")}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {isInviteLinkWithEquipment ? "Confirm the details below." : "You can confirm the details below or change them as needed."}
              </p>
            </div>
          )}

          {!isDemcoGrainCart && (
          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] md:gap-x-6 gap-y-2 md:items-start">
            <Label className="md:pt-2.5">Equipment Type</Label>
            <div className="min-w-0 relative">
            {/* Search by keyword (e.g. semi, forklift, reefer) */}
            <Input
              type="text"
              placeholder="Search equipment (e.g. semi, forklift, reefer)..."
              value={equipmentSearchQuery}
              onChange={(e) => setEquipmentSearchQuery(e.target.value)}
              className="w-full mb-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isEquipmentTypeSelectorLocked}
            />
            {!isEquipmentTypeSelectorLocked && equipmentSearchResults.length > 0 && (
              <ul className="absolute z-10 w-full mt-0 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto">
                {equipmentSearchResults.map((opt, idx) => (
                  <li key={idx}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                      onClick={() => {
                        equipmentTypeUserTouchedRef.current = true;
                        setForm({
                          ...form,
                          equipment_type_id: opt.equipment_type.id,
                          equipment_subtype_id: opt.equipment_subtype?.id ?? null,
                        });
                        setEquipmentSearchQuery("");
                        setEquipmentSearchResults([]);
                      }}
                    >
                      {opt.equipment_type.name}{opt.equipment_subtype ? ` — ${opt.equipment_subtype.name}` : ""}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <select
              value={intakePending || singleTypeLockPending ? "" : (form.equipment_type_id ?? "")}
              onChange={(e) => {
                equipmentTypeUserTouchedRef.current = true;
                const val = e.target.value === "" ? null : Number(e.target.value);
                const selectedType = val != null ? equipmentTypes.find((et: { id: number; subtypes?: { id: number; name: string }[] }) => et.id === val) : null;
                const subtypes = selectedType?.subtypes ?? [];
                const singleSubtypeId = subtypes.length === 1 ? subtypes[0].id : null;
                setForm({
                  ...form,
                  equipment_type_id: val,
                  equipment_subtype_id: val != null ? singleSubtypeId : null,
                });
              }}
              className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${fieldErrors.equipment_type_id ? "border-red-500" : "border-gray-300"}`}
              disabled={isEquipmentTypeSelectorLocked}
            >
              {intakePending || singleTypeLockPending ? (
                <option value="">Loading equipment types...</option>
              ) : (
                <>
                  <option value="">Select equipment type...</option>
                  {equipmentTypes.map((et: { id: number; name: string }) => (
                    <option key={et.id} value={et.id}>{et.name}</option>
                  ))}
                </>
              )}
            </select>
            {fieldErrors.equipment_type_id && (
              <p className="text-red-500 text-sm mt-1">{fieldErrors.equipment_type_id}</p>
            )}
            {form.equipment_type_id && (() => {
              const selected = equipmentTypes.find((et: { id: number }) => et.id === form.equipment_type_id);
              const examples = selected?.examples ?? [];
              if (examples.length === 0) return null;
              return (
                <p className="text-sm text-gray-600 mt-1">
                  Examples: {examples.map((ex: { label: string }) => ex.label).join(", ")}
                </p>
              );
            })()}
            </div>
          </div>
          )}

          {/* Equipment Subtype (only when type has more than one subtype) */}
          {form.equipment_type_id && !intakeProgram.hide_equipment_subtype && (() => {
            const selectedType = equipmentTypes.find((et: { id: number; subtypes?: { id: number; name: string }[] }) => et.id === form.equipment_type_id);
            const subtypes = selectedType?.subtypes ?? [];
            if (subtypes.length <= 1) return null;
            return (
              <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] md:gap-x-6 gap-y-2 md:items-start">
                <Label className="md:pt-2.5">Equipment Subtype</Label>
                <div className="min-w-0">
                  <select
                    value={form.equipment_subtype_id ?? ""}
                    onChange={(e) => {
                      const val = e.target.value === "" ? null : Number(e.target.value);
                      setForm({ ...form, equipment_subtype_id: val });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={isInviteLinkWithEquipment}
                  >
                    <option value="">Select subtype (optional)...</option>
                    {subtypes.map((sub: { id: number; name: string }) => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })()}

          {/* Year / Mileage / Hours based on equipment type underwriting_signals */}
          {(form.equipment_type_id || isDemcoGrainCart) && (() => {
            const selectedType = equipmentTypes.find((et: { id: number; slug?: string; underwriting_signals?: string[] }) => et.id === form.equipment_type_id);
            const signals: string[] = selectedType?.underwriting_signals ?? [];
            const noMileageSlugs = ["waste-recycling-environmental-equipment", "sanitation-restroom-trailers"];
            const programFields = intakeProgram.equipment_fields ?? [];
            const showYear = programFields.includes("year") || signals.includes("year");
            const showMileage =
              !intakeProgram.hide_equipment_hours &&
              signals.includes("mileage") &&
              !noMileageSlugs.includes(selectedType?.slug ?? "");
            const showHours = !intakeProgram.hide_equipment_hours && signals.includes("hours");
            if (!showYear && !showMileage && !showHours) return null;
            return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {showYear && (
                  <div>
                    <Label>Model Year</Label>
                    <Input
                      type="number"
                      min={1900}
                      max={new Date().getFullYear() + 2}
                      placeholder="e.g. 2022"
                      value={form.equipment_year ?? ""}
                      onChange={(e) => setForm({ ...form, equipment_year: e.target.value ? e.target.value : "" })}
                      className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${fieldErrors.equipment_year ? "border-red-500" : "border-gray-300"}`}
                      disabled={isInviteLinkWithEquipment}
                    />
                    {fieldErrors.equipment_year && (
                      <p className="text-red-500 text-sm mt-1">{fieldErrors.equipment_year}</p>
                    )}
                  </div>
                )}
                {showMileage && (
                  <div>
                    <Label>Mileage</Label>
                    <Input
                      type="number"
                      min={0}
                      placeholder="e.g. 150000"
                      value={form.equipment_mileage ?? ""}
                      onChange={(e) => setForm({ ...form, equipment_mileage: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${fieldErrors.equipment_mileage ? "border-red-500" : "border-gray-300"}`}
                      disabled={isInviteLinkWithEquipment}
                    />
                    {fieldErrors.equipment_mileage && (
                      <p className="text-red-500 text-sm mt-1">{fieldErrors.equipment_mileage}</p>
                    )}
                  </div>
                )}
                {showHours && (
                  <div>
                    <Label>Hours</Label>
                    <Input
                      type="number"
                      min={0}
                      placeholder="e.g. 5000"
                      value={form.equipment_hours ?? ""}
                      onChange={(e) => setForm({ ...form, equipment_hours: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      disabled={isInviteLinkWithEquipment}
                    />
                  </div>
                )}
              </div>
            );
          })()}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {!intakeProgram.hide_equipment_name && (
            <div className="md:col-span-2">
              <Label>Equipment Name</Label>
              <Input 
                required 
                value={form.equipment_name} 
                onChange={(e) => {
                  setForm({ ...form, equipment_name: e.target.value });
                  if (fieldErrors.equipment_name) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.equipment_name;
                      return newErrors;
                    });
                  }
                }}
                className={`${fieldErrors.equipment_name ? "border-red-500" : ""} ${isInviteLinkWithEquipment ? "disabled:bg-gray-100 disabled:cursor-not-allowed" : ""}`.trim()}
                disabled={isInviteLinkWithEquipment}
              />
              {fieldErrors.equipment_name && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.equipment_name}</p>
              )}
            </div>
            )}
            <div>
              <Label>Equipment Price ($)</Label>
              <Input
                required
                value={formatCurrency(form.equipment_price)}
                onChange={(e) => {
                  const digits = toDigits(e.target.value);
                  setForm({ ...form, equipment_price: digits });
                  if (fieldErrors.equipment_price) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.equipment_price;
                      return newErrors;
                    });
                  }
                }}
                placeholder="0"
                className={`${fieldErrors.equipment_price ? "border-red-500" : ""} ${isInviteLinkWithEquipment ? "disabled:bg-gray-100 disabled:cursor-not-allowed" : ""}`.trim()}
                disabled={isInviteLinkWithEquipment}
              />
              {fieldErrors.equipment_price && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.equipment_price}</p>
              )}
            </div>
          </div>

          {(intakeData?.primary_equipment_custom_field_defs ?? []).length > 0 && (
            <div className="space-y-3 border-t border-gray-100 pt-4">
              <p className="text-sm font-medium text-gray-800">Additional equipment details (optional)</p>
              {(intakeData?.primary_equipment_custom_field_defs ?? []).map((d) => (
                <div key={d.key}>
                  <Label>{d.label}</Label>
                  <Input
                    value={(form.primary_equipment_custom_values || {})[d.key] ?? ""}
                    maxLength={144}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm({
                        ...form,
                        primary_equipment_custom_values: {
                          ...(form.primary_equipment_custom_values || {}),
                          [d.key]: v,
                        },
                      });
                    }}
                    className="mt-1"
                    disabled={isInviteLinkWithEquipment}
                  />
                </div>
              ))}
            </div>
          )}

          {(showCdlGateQuestions || showFleetGateQuestions) && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 space-y-4 mb-4">
              <p className="text-sm font-medium text-gray-900">Equipment screening</p>
              <p className="text-xs text-gray-600">
                These answers help match your application to the right programs. They are required for your equipment type or industry.
              </p>
              {showCdlGateQuestions && (
                <div className="space-y-3 pt-1 border-t border-amber-200/80">
                  <Label className="text-sm font-medium">
                    {cdlQuestionsMultiOwner
                      ? "Does at least one owner hold a valid CDL?"
                      : "Do you have a CDL?"}{" "}
                    *
                  </Label>
                  <div className="flex gap-4">
                    <label className="flex items-center text-sm">
                      <input
                        type="radio"
                        name="prequal_gate_cdl_held"
                        className="mr-2"
                        checked={form.prequal_gate_cdl_held === "yes"}
                        onChange={() => {
                          setForm({ ...form, prequal_gate_cdl_held: "yes" });
                          if (fieldErrors.prequal_gate_cdl_held) {
                            setFieldErrors((prev) => {
                              const n = { ...prev };
                              delete n.prequal_gate_cdl_held;
                              return n;
                            });
                          }
                        }}
                      />
                      Yes
                    </label>
                    <label className="flex items-center text-sm">
                      <input
                        type="radio"
                        name="prequal_gate_cdl_held"
                        className="mr-2"
                        checked={form.prequal_gate_cdl_held === "no"}
                        onChange={() => {
                          setForm({
                            ...form,
                            prequal_gate_cdl_held: "no",
                            prequal_gate_cdl_over_one_year: undefined,
                          });
                          if (fieldErrors.prequal_gate_cdl_held) {
                            setFieldErrors((prev) => {
                              const n = { ...prev };
                              delete n.prequal_gate_cdl_held;
                              return n;
                            });
                          }
                        }}
                      />
                      No
                    </label>
                  </div>
                  {fieldErrors.prequal_gate_cdl_held && (
                    <p className="text-red-500 text-sm">{fieldErrors.prequal_gate_cdl_held}</p>
                  )}
                  {form.prequal_gate_cdl_held === "yes" && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        {cdlQuestionsMultiOwner
                          ? "Has that CDL been held for longer than one year?"
                          : "Have you had it for longer than one year?"}{" "}
                        *
                      </Label>
                      <div className="flex gap-4">
                        <label className="flex items-center text-sm">
                          <input
                            type="radio"
                            name="prequal_gate_cdl_over_one_year"
                            className="mr-2"
                            checked={form.prequal_gate_cdl_over_one_year === "yes"}
                            onChange={() => {
                              setForm({ ...form, prequal_gate_cdl_over_one_year: "yes" });
                              if (fieldErrors.prequal_gate_cdl_over_one_year) {
                                setFieldErrors((prev) => {
                                  const n = { ...prev };
                                  delete n.prequal_gate_cdl_over_one_year;
                                  return n;
                                });
                              }
                            }}
                          />
                          Yes
                        </label>
                        <label className="flex items-center text-sm">
                          <input
                            type="radio"
                            name="prequal_gate_cdl_over_one_year"
                            className="mr-2"
                            checked={form.prequal_gate_cdl_over_one_year === "no"}
                            onChange={() => {
                              setForm({ ...form, prequal_gate_cdl_over_one_year: "no" });
                              if (fieldErrors.prequal_gate_cdl_over_one_year) {
                                setFieldErrors((prev) => {
                                  const n = { ...prev };
                                  delete n.prequal_gate_cdl_over_one_year;
                                  return n;
                                });
                              }
                            }}
                          />
                          No
                        </label>
                      </div>
                      {fieldErrors.prequal_gate_cdl_over_one_year && (
                        <p className="text-red-500 text-sm">{fieldErrors.prequal_gate_cdl_over_one_year}</p>
                      )}
                    </div>
                  )}
                  <div className="space-y-2 pt-1">
                    <Label className="text-sm font-medium">
                      Are you a first time buyer? *
                    </Label>
                    <div className="flex gap-4">
                      <label className="flex items-center text-sm">
                        <input
                          type="radio"
                          name="prequal_gate_first_time_oo"
                          className="mr-2"
                          checked={form.prequal_gate_first_time_oo === "yes"}
                          onChange={() => {
                            setForm({ ...form, prequal_gate_first_time_oo: "yes" });
                            if (fieldErrors.prequal_gate_first_time_oo) {
                              setFieldErrors((prev) => {
                                const n = { ...prev };
                                delete n.prequal_gate_first_time_oo;
                                return n;
                              });
                            }
                          }}
                        />
                        Yes
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="radio"
                          name="prequal_gate_first_time_oo"
                          className="mr-2"
                          checked={form.prequal_gate_first_time_oo === "no"}
                          onChange={() => {
                            setForm({ ...form, prequal_gate_first_time_oo: "no" });
                            if (fieldErrors.prequal_gate_first_time_oo) {
                              setFieldErrors((prev) => {
                                const n = { ...prev };
                                delete n.prequal_gate_first_time_oo;
                                return n;
                              });
                            }
                          }}
                        />
                        No
                      </label>
                    </div>
                    {fieldErrors.prequal_gate_first_time_oo && (
                      <p className="text-red-500 text-sm">{fieldErrors.prequal_gate_first_time_oo}</p>
                    )}
                  </div>
                </div>
              )}
              {showFleetGateQuestions && (
                <div className="space-y-3 pt-1 border-t border-amber-200/80">
                  <div>
                    <Label className="text-sm font-medium">How many trucks does the business currently operate? *</Label>
                    <Input
                      type="number"
                      min={0}
                      className={`mt-1 max-w-xs ${fieldErrors.prequal_gate_fleet_total ? "border-red-500" : ""}`}
                      value={form.prequal_gate_fleet_total}
                      onWheel={(e) => {
                        e.currentTarget.blur();
                        e.preventDefault();
                      }}
                      onChange={(e) => {
                        const v = e.target.value;
                        const n = parseInt(v.replace(/\D/g, "") || "0", 10);
                        setForm((prev) => ({
                          ...prev,
                          prequal_gate_fleet_total: v,
                          ...(n < 1
                            ? { prequal_gate_safer_all: undefined, prequal_gate_safer_reporting: "" }
                            : {}),
                        }));
                        if (fieldErrors.prequal_gate_fleet_total) {
                          setFieldErrors((prev) => {
                            const ne = { ...prev };
                            delete ne.prequal_gate_fleet_total;
                            return ne;
                          });
                        }
                        if (n < 1) {
                          setFieldErrors((prev) => {
                            const ne = { ...prev };
                            delete ne.prequal_gate_safer_all;
                            delete ne.prequal_gate_safer_reporting;
                            return ne;
                          });
                        }
                      }}
                    />
                    {fieldErrors.prequal_gate_fleet_total && (
                      <p className="text-red-500 text-sm mt-1">{fieldErrors.prequal_gate_fleet_total}</p>
                    )}
                  </div>
                  {fleetGateTruckCount >= 1 && (
                    <>
                      <div>
                        <Label className="text-sm font-medium">Are all the trucks registered with DOT? *</Label>
                        <div className="flex gap-4 mt-2">
                          <label className="flex items-center text-sm">
                            <input
                              type="radio"
                              name="prequal_gate_safer_all"
                              className="mr-2"
                              checked={form.prequal_gate_safer_all === "yes"}
                              onChange={() => {
                                setForm({ ...form, prequal_gate_safer_all: "yes", prequal_gate_safer_reporting: "" });
                                if (fieldErrors.prequal_gate_safer_all) {
                                  setFieldErrors((prev) => {
                                    const n = { ...prev };
                                    delete n.prequal_gate_safer_all;
                                    return n;
                                  });
                                }
                              }}
                            />
                            Yes
                          </label>
                          <label className="flex items-center text-sm">
                            <input
                              type="radio"
                              name="prequal_gate_safer_all"
                              className="mr-2"
                              checked={form.prequal_gate_safer_all === "no"}
                              onChange={() => {
                                setForm({ ...form, prequal_gate_safer_all: "no" });
                                if (fieldErrors.prequal_gate_safer_all) {
                                  setFieldErrors((prev) => {
                                    const n = { ...prev };
                                    delete n.prequal_gate_safer_all;
                                    return n;
                                  });
                                }
                              }}
                            />
                            No
                          </label>
                        </div>
                        {fieldErrors.prequal_gate_safer_all && (
                          <p className="text-red-500 text-sm mt-1">{fieldErrors.prequal_gate_safer_all}</p>
                        )}
                      </div>
                      {form.prequal_gate_safer_all === "no" && (
                        <div>
                          <Label className="text-sm font-medium">How many trucks are registered with DOT? *</Label>
                          <Input
                            type="number"
                            min={0}
                            className={`mt-1 max-w-xs ${fieldErrors.prequal_gate_safer_reporting ? "border-red-500" : ""}`}
                            value={form.prequal_gate_safer_reporting}
                            onWheel={(e) => {
                              e.currentTarget.blur();
                              e.preventDefault();
                            }}
                            onChange={(e) => {
                              setForm({ ...form, prequal_gate_safer_reporting: e.target.value });
                              if (fieldErrors.prequal_gate_safer_reporting) {
                                setFieldErrors((prev) => {
                                  const n = { ...prev };
                                  delete n.prequal_gate_safer_reporting;
                                  return n;
                                });
                              }
                            }}
                          />
                          {fieldErrors.prequal_gate_safer_reporting && (
                            <p className="text-red-500 text-sm mt-1">{fieldErrors.prequal_gate_safer_reporting}</p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button variant="secondary" onClick={() => setStep(3)}>
              Back
            </Button>
            <Button
              onClick={async () => {
                const errors = validateStep4Equipment();
                if (Object.keys(errors).length > 0) {
                  setFieldErrors(errors);
                  setTimeout(() => scrollToFirstError(), 100);
                  return;
                }
                setFieldErrors({});
                try {
                  await saveStep.mutateAsync(4);
                  await confirmEquipment.mutateAsync();
                } catch {
                  // errors handled by mutations
                }
              }}
              className="bg-blue-600 text-white"
              disabled={saveStep.isPending || confirmEquipment.isPending}
            >
              {confirmEquipment.isPending ? "Confirming..." : "Confirm selection"}
            </Button>
          </div>
        </div>
      )}

      {/* Signature step: draw signature then POST to sign endpoint; or full-screen loading while sign is pending */}
      {showSignatureStep && (submitAwaitingSignature || signMutation.isPending) && (
        <div className="min-h-[320px] flex flex-col items-center justify-center gap-4 bg-white rounded-xl border p-8 text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary rounded-full animate-spin" aria-hidden />
          <h2 className="text-lg font-semibold text-gray-900">
            {submitAwaitingSignature ? "Saving your application" : "Submitting your signature"}
          </h2>
          <p className="text-sm text-gray-600 max-w-sm">
            {submitAwaitingSignature
              ? "Please wait a moment before signing."
              : "Please wait while we save your signature."}
          </p>
        </div>
      )}
      {showSignatureStep && !submitAwaitingSignature && !signMutation.isPending && (
        <div ref={signatureSectionRef} className="space-y-4 bg-white rounded-xl border p-4">
          <h2 className="text-lg font-semibold">Sign your application</h2>
          <p className="text-sm text-gray-600">Draw your signature in the box below, then click Submit.</p>
          <div className="border rounded-lg overflow-hidden bg-gray-50">
            <canvas
              ref={canvasRef}
              width={500}
              height={200}
              className="w-full max-w-full touch-none cursor-crosshair border-b border-gray-200"
              style={{ touchAction: "none" }}
              onPointerDown={(e) => {
                const canvas = canvasRef.current;
                if (!canvas) return;
                const ctx = canvas.getContext("2d");
                if (!ctx) return;
                ctx.strokeStyle = "#000";
                ctx.lineWidth = 2;
                ctx.lineCap = "round";
                ctx.lineJoin = "round";
                isDrawingRef.current = true;
                const rect = canvas.getBoundingClientRect();
                const scaleX = canvas.width / rect.width;
                const scaleY = canvas.height / rect.height;
                const x = (e.clientX - rect.left) * scaleX;
                const y = (e.clientY - rect.top) * scaleY;
                ctx.beginPath();
                ctx.moveTo(x, y);
              }}
              onPointerMove={(e) => {
                if (!isDrawingRef.current) return;
                const canvas = canvasRef.current;
                if (!canvas) return;
                const ctx = canvas.getContext("2d");
                if (!ctx) return;
                const rect = canvas.getBoundingClientRect();
                const scaleX = canvas.width / rect.width;
                const scaleY = canvas.height / rect.height;
                const x = (e.clientX - rect.left) * scaleX;
                const y = (e.clientY - rect.top) * scaleY;
                ctx.lineTo(x, y);
                ctx.stroke();
              }}
              onPointerUp={() => { isDrawingRef.current = false; }}
              onPointerLeave={() => { isDrawingRef.current = false; }}
            />
          </div>
          <p className="text-xs text-gray-600">
            By clicking <span className="font-medium">Submit signature</span>, you agree to the terms shown below.
          </p>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const canvas = canvasRef.current;
                if (canvas) {
                  const ctx = canvas.getContext("2d");
                  if (ctx) {
                    ctx.fillStyle = "#ffffff";
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                  }
                }
              }}
            >
              Clear
            </Button>
            <Button
              onClick={() => {
                const canvas = canvasRef.current;
                if (!canvas) {
                  toast.error("Signature area not ready.");
                  return;
                }
                const dataUrl = canvas.toDataURL("image/png");
                const ctx = canvas.getContext("2d");
                if (ctx) {
                  const pixelData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
                  let hasStroke = false;
                  for (let i = 0; i < pixelData.length; i += 4) {
                    const r = pixelData[i];
                    const g = pixelData[i + 1];
                    const b = pixelData[i + 2];
                    const a = pixelData[i + 3];
                    if (a > 0 && (r < 250 || g < 250 || b < 250)) {
                      hasStroke = true;
                      break;
                    }
                  }
                  if (!hasStroke) {
                    toast.error("Please draw your signature first.");
                    return;
                  }
                }
                signMutation.mutate(dataUrl);
              }}
              disabled={signMutation.isPending}
            >
              {signMutation.isPending ? "Submitting..." : "Submit signature"}
            </Button>
          </div>
          <div className="space-y-2 pt-2 border-t border-gray-100">
            <div
              className={`text-xs text-gray-600 whitespace-pre-line ${!showFullTerms ? "line-clamp-4 sm:line-clamp-none" : ""}`}
            >
              {intakeData?.consent_language?.content || ""}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowFullTerms(!showFullTerms)}
              className="text-xs sm:hidden text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              {showFullTerms ? "Show Less" : "Show More"}
            </Button>
          </div>
        </div>
      )}
      </div>
      <SiteLegalFooter
        openPrivacyInNewTab
        className="sticky bottom-0 mt-4 -mx-2 bg-white/95 border-t border-gray-100 backdrop-blur-sm"
      />
    </div>
  );
}



